// Phase 2 V2 — Avatar Wizard
//
// Three distinct paths for picking the presenter, inspired by Higgsfield
// Soul Cast and Freepik Custom Characters:
//
//   1. Preset — grid of curated Fluxi avatars (pre-rendered .webp in S3).
//      One click, no waiting.
//   2. Custom — interactive builder with a LIVE portrait preview on the
//      left. Every change to gender/age/skin/hair/vibe/setting debounces
//      and hits the backend /ads/videos/avatar/preview to regenerate the
//      image. User commits with "Usar este avatar".
//   3. Upload — placeholder for Phase C (face training from photos).
//
// On commit, we return an UGCAvatarConfig to the parent. For presets we
// read the config embedded in the preset entry. For custom we return the
// builder state. The portrait URL is ALSO returned so the sidebar can
// show the mini-chip ("Presenter: Valentina · Cambiar") without
// regenerating the image on mount.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BookmarkPlus,
  Camera,
  Check,
  Loader2,
  Pencil,
  RefreshCw,
  SlidersHorizontal,
  Sparkles,
  Star,
  Trash2,
  Upload,
  UserCircle2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { toBase64 } from "@/utils/utils";
import { getAvatarRecommendationRole, previewAvatar } from "@/services/ads/videos/videoDraftService";
import type { AvatarRecommendationItem } from "@/services/ads/videos/videoDraft.types";
import { uploadImagesToS3 } from "@/services/s3/s3Service";
import { useMyAvatars, type ParsedSavedAvatar } from "../hooks/useMyAvatars";
import type { UGCAvatarConfig } from "../types/videoStudio.types";
import {
  AVATAR_AGE_OPTIONS,
  AVATAR_GENDER_OPTIONS,
  AVATAR_HAIR_COLOR_OPTIONS,
  AVATAR_HAIR_OPTIONS,
  AVATAR_SETTING_OPTIONS,
  PHOTOGRAPHIC_STYLE_OPTIONS,
  SKIN_FINISH_OPTIONS,
  AVATAR_SKIN_OPTIONS,
  AVATAR_VIBE_OPTIONS,
} from "../types/videoStudio.types";

// ─────────────────────────────────────────────────────────────────
// Preset catalog — stays in sync with the .webp files in S3. Each preset
// embeds the avatar_config that
// produced it so when the user picks one, the draft request sends the
// right descriptors to the director (not just the URL).
// ─────────────────────────────────────────────────────────────────
interface PresetAvatar {
  id: string;
  name: string;
  subtitle: string;
  thumbnail: string;
  config: UGCAvatarConfig;
}

const AVATAR_RECOMMENDATION_LOADING_CARDS = [
  {
    title: "Creando avatar #1",
    steps: ["Analizando el dolor del cliente", "Buscando un rostro cotidiano", "Preparando el primer retrato"],
  },
  {
    title: "Creando avatar #2",
    steps: ["Leyendo el contexto del producto", "Ajustando una escena de uso real", "Creando una identidad reusable"],
  },
  {
    title: "Creando avatar #3",
    steps: ["Traduciendo la promesa", "Buscando una energia de alivio", "Preparando el tercer retrato"],
  },
  {
    title: "Creando avatar #4",
    steps: ["Definiendo un perfil confiable", "Evitando look publicitario", "Afinando expresión y encuadre"],
  },
];
const AVATAR_RECOMMENDATION_ROLES = ["problem_fit", "routine_fit", "aspiration_fit", "trust_fit"] as const;
const AVATAR_RECOMMENDATION_MAX_ATTEMPTS = 3;
const FALLBACK_IMAGE_PATH = "/fallback-image.png";

function getAvatarCreativeTitle(role?: string | null) {
  const index = AVATAR_RECOMMENDATION_ROLES.findIndex((item) => item === role);
  return `Avatar #${index >= 0 ? index + 1 : 1}`;
}

function isUsableAvatarPreviewUrl(url?: string | null) {
  const normalized = url?.trim().toLowerCase() ?? "";
  return normalized.length > 0 && !normalized.includes(FALLBACK_IMAGE_PATH);
}

function shortProductName(value?: string | null) {
  const normalized = (value ?? "").trim().replace(/\s+/g, " ");
  if (!normalized) return "";
  const primary = normalized.split(/\s[-–—|]\s|[,;]/)[0]?.trim() || normalized;
  if (primary.length <= 34) return primary;
  const cut = primary.slice(0, 34).trimEnd();
  const lastSpace = cut.lastIndexOf(" ");
  return lastSpace >= 16 ? cut.slice(0, lastSpace) : cut;
}

function createAvatarRecommendationRunId() {
  return `avatar-run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function AvatarThumbnailImage({
  src,
  alt,
  imageClassName,
}: {
  src: string;
  alt: string;
  imageClassName?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  return (
    <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-slate-100">
      {!loaded && !failed && (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-y-0 -left-1/2 w-1/2 skew-x-[-18deg] bg-white/70 blur-xl animate-shine" />
        </div>
      )}

      {failed ? (
        <div className="absolute inset-0 flex items-center justify-center text-indigo-300">
          <UserCircle2 className="h-10 w-10" />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-300",
            loaded ? "opacity-100" : "opacity-0",
            imageClassName
          )}
          loading="eager"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

const AVATAR_CDN_BASE = "https://fluxi.co/prod/assets/dev/assets/avatars";

const PRESETS: PresetAvatar[] = [
  {
    id: "valentina",
    name: "Valentina",
    subtitle: "Mujer · 22 · Casual",
    thumbnail: `${AVATAR_CDN_BASE}/valentina.webp`,
    config: { gender: "female", age_range: "18-25", skin_tone: "medium", hair: "long-wavy", hair_color: "brown", vibe: "casual-natural", setting: "bedroom" },
  },
  {
    id: "sofia",
    name: "Sofía",
    subtitle: "Mujer · 29 · Caribeña",
    thumbnail: `${AVATAR_CDN_BASE}/sofia.webp`,
    // Phase 6 V4d (Apr 21 2026) — portrait replaced by Valeria (Barranquilla
    // costeña, curly hair 3B-3C, gold chain, kitchen with Guadalupe calendar)
    // from the user's reference examples. Config updated accordingly.
    config: { gender: "female", age_range: "25-35", skin_tone: "tan", hair: "long-curly", hair_color: "brown", vibe: "professional", setting: "kitchen" },
  },
  {
    id: "camila",
    name: "Camila",
    subtitle: "Mujer · 26 · Suave",
    thumbnail: `${AVATAR_CDN_BASE}/camila.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "long-curly", hair_color: "black", vibe: "glam", setting: "bathroom" },
  },
  {
    id: "lucia",
    name: "Lucía",
    subtitle: "Mujer · 26 · Paisa nerd",
    thumbnail: `${AVATAR_CDN_BASE}/lucia.webp`,
    // Phase 6 V4d — portrait replaced by Mariana (Medellín paisa nerd, UNAL
    // burgundy hoodie, wire-frame glasses, frizzy wavy chestnut hair, bedroom
    // student desk setup) from the user's reference examples.
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "long-wavy", hair_color: "brown", vibe: "casual-natural", setting: "bedroom" },
  },
  {
    id: "mateo",
    name: "Mateo",
    subtitle: "Hombre · 22 · Casual",
    thumbnail: `${AVATAR_CDN_BASE}/mateo.webp`,
    config: { gender: "male", age_range: "18-25", skin_tone: "medium", hair: "short-straight", hair_color: "brown", vibe: "casual-natural", setting: "home-office" },
  },
  {
    id: "diego",
    name: "Diego",
    subtitle: "Hombre · 30 · Profesional",
    thumbnail: `${AVATAR_CDN_BASE}/diego.webp`,
    config: { gender: "male", age_range: "25-35", skin_tone: "medium", hair: "short-straight", hair_color: "black", vibe: "professional", setting: "office" },
  },
  {
    id: "rafael",
    name: "Rafael",
    subtitle: "Hombre · 28 · Emprendedor",
    thumbnail: `${AVATAR_CDN_BASE}/rafael.webp`,
    // Phase 6 V4d — portrait replaced by Andrés (Bogotá small-business
    // owner, phone case shop, black CellShop tee, medium skin cool undertones,
    // chaotic funny personality) from the user's reference examples.
    config: { gender: "male", age_range: "25-35", skin_tone: "medium", hair: "short-straight", hair_color: "brown", vibe: "casual-natural", setting: "office" },
  },
  {
    id: "santiago",
    name: "Santiago",
    subtitle: "Hombre · 28 · Moderno",
    thumbnail: `${AVATAR_CDN_BASE}/santiago.webp`,
    config: { gender: "male", age_range: "25-35", skin_tone: "dark", hair: "short-curly", hair_color: "black", vibe: "casual-natural", setting: "bedroom" },
  },
  {
    id: "paula",
    name: "Paula",
    subtitle: "Mujer · 24 · Natural",
    thumbnail: `${AVATAR_CDN_BASE}/paula.webp`,
    config: { gender: "female", age_range: "18-25", skin_tone: "tan", hair: "long-curly", hair_color: "brown", vibe: "casual-natural", setting: "kitchen" },
  },
  {
    id: "valeria",
    name: "Valeria",
    subtitle: "Mujer · 30 · Costeña",
    thumbnail: `${AVATAR_CDN_BASE}/valeria.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "tan", hair: "long-curly", hair_color: "brown", vibe: "professional", setting: "kitchen" },
  },
  {
    id: "mariana",
    name: "Mariana",
    subtitle: "Mujer · 26 · Universitaria",
    thumbnail: `${AVATAR_CDN_BASE}/mariana.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "long-wavy", hair_color: "brown", vibe: "casual-natural", setting: "home-office" },
  },
  {
    id: "isabella",
    name: "Isabella",
    subtitle: "Mujer · 31 · Wellness",
    thumbnail: `${AVATAR_CDN_BASE}/isabella.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "tan", hair: "bun", hair_color: "brown", vibe: "sporty", setting: "gym" },
  },
  {
    id: "renata",
    name: "Renata",
    subtitle: "Mujer · 32 · Cálida",
    thumbnail: `${AVATAR_CDN_BASE}/renata.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "long-wavy", hair_color: "brown", vibe: "casual-natural", setting: "bedroom" },
  },
  {
    id: "daniela",
    name: "Daniela",
    subtitle: "Mujer · 27 · Suave",
    thumbnail: `${AVATAR_CDN_BASE}/daniela.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "bun", hair_color: "brown", vibe: "casual-natural", setting: "bedroom" },
  },
  {
    id: "laura",
    name: "Laura",
    subtitle: "Mujer · 25 · Cercana",
    thumbnail: `${AVATAR_CDN_BASE}/laura.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "medium", hair: "long-straight", hair_color: "brown", vibe: "casual-natural", setting: "bedroom" },
  },
  {
    id: "juli",
    name: "Juli",
    subtitle: "Mujer · 29 · Fitness",
    thumbnail: `${AVATAR_CDN_BASE}/juli.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "tan", hair: "ponytail", hair_color: "brown", vibe: "sporty", setting: "gym" },
  },
  {
    id: "yuli",
    name: "Yuli",
    subtitle: "Mujer · 35 · Profesional",
    thumbnail: `${AVATAR_CDN_BASE}/yuli.webp`,
    config: { gender: "female", age_range: "35-45", skin_tone: "light", hair: "long-straight", hair_color: "black", vibe: "professional", setting: "outdoor" },
  },
  {
    id: "aura",
    name: "Aura",
    subtitle: "Mujer · 42 · Serena",
    thumbnail: `${AVATAR_CDN_BASE}/aura.webp`,
    config: { gender: "female", age_range: "35-45", skin_tone: "dark", hair: "short-curly", hair_color: "black", vibe: "casual-natural", setting: "kitchen" },
  },
  {
    id: "silvia",
    name: "Silvia",
    subtitle: "Mujer · 45+ · Directa",
    thumbnail: `${AVATAR_CDN_BASE}/silvia.webp`,
    config: { gender: "female", age_range: "45+", skin_tone: "medium", hair: "short-curly", hair_color: "black", vibe: "casual-natural", setting: "kitchen" },
  },
  {
    id: "dona-lucia",
    name: "Doña Lucía",
    subtitle: "Mujer · 45+ · Cocina",
    thumbnail: `${AVATAR_CDN_BASE}/dona-lucia.webp`,
    config: { gender: "female", age_range: "45+", skin_tone: "light", hair: "bun", hair_color: "gray", vibe: "casual-natural", setting: "kitchen" },
  },
  {
    id: "clara",
    name: "Clara",
    subtitle: "Mujer · 45+ · Hogar",
    thumbnail: `${AVATAR_CDN_BASE}/clara.webp`,
    config: { gender: "female", age_range: "45+", skin_tone: "light", hair: "long-wavy", hair_color: "brown", vibe: "casual-natural", setting: "kitchen" },
  },
  {
    id: "andres",
    name: "Andrés",
    subtitle: "Hombre · 28 · Emprendedor",
    thumbnail: `${AVATAR_CDN_BASE}/andres.webp`,
    config: { gender: "male", age_range: "25-35", skin_tone: "medium", hair: "short-straight", hair_color: "black", vibe: "casual-natural", setting: "office" },
  },
  {
    id: "carlos",
    name: "Carlos",
    subtitle: "Hombre · 34 · Técnico",
    thumbnail: `${AVATAR_CDN_BASE}/carlos.webp`,
    config: { gender: "male", age_range: "25-35", skin_tone: "light", hair: "short-straight", hair_color: "black", vibe: "professional", setting: "office" },
  },
  {
    id: "emi",
    name: "Emi",
    subtitle: "Hombre · 24 · Casual",
    thumbnail: `${AVATAR_CDN_BASE}/emi.webp`,
    config: { gender: "male", age_range: "18-25", skin_tone: "medium", hair: "short-straight", hair_color: "red", vibe: "casual-natural", setting: "bedroom" },
  },
  {
    id: "pablo",
    name: "Pablo",
    subtitle: "Hombre · 38 · Andino",
    thumbnail: `${AVATAR_CDN_BASE}/pablo.webp`,
    config: { gender: "male", age_range: "35-45", skin_tone: "light", hair: "short-straight", hair_color: "black", vibe: "professional", setting: "kitchen" },
  },
  {
    id: "natalia",
    name: "Natalia",
    subtitle: "Mujer · 29 · Social",
    thumbnail: `${AVATAR_CDN_BASE}/natalia.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "tan", hair: "long-curly", hair_color: "brown", vibe: "glam", setting: "minimal" },
  },
  {
    id: "ximena",
    name: "Ximena",
    subtitle: "Mujer · 27 · Trendy",
    thumbnail: `${AVATAR_CDN_BASE}/ximena.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "long-wavy", hair_color: "black", vibe: "glam", setting: "minimal" },
  },
  {
    id: "alejandra",
    name: "Alejandra",
    subtitle: "Mujer · 34 · Lifestyle",
    thumbnail: `${AVATAR_CDN_BASE}/alejandra.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "long-wavy", hair_color: "brown", vibe: "casual-natural", setting: "minimal" },
  },
  {
    id: "marcela",
    name: "Marcela",
    subtitle: "Mujer · 36 · Directa",
    thumbnail: `${AVATAR_CDN_BASE}/marcela.webp`,
    config: { gender: "female", age_range: "35-45", skin_tone: "tan", hair: "long-wavy", hair_color: "brown", vibe: "professional", setting: "minimal" },
  },
  {
    id: "karina",
    name: "Karina",
    subtitle: "Mujer · 30 · Urbana",
    thumbnail: `${AVATAR_CDN_BASE}/karina.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "light", hair: "long-straight", hair_color: "black", vibe: "professional", setting: "home-office" },
  },
  {
    id: "fernando",
    name: "Fernando",
    subtitle: "Hombre · 39 · Sobrio",
    thumbnail: `${AVATAR_CDN_BASE}/fernando.webp`,
    config: { gender: "male", age_range: "35-45", skin_tone: "medium", hair: "short-straight", hair_color: "black", vibe: "casual-natural", setting: "home-office" },
  },
  {
    id: "esteban",
    name: "Esteban",
    subtitle: "Hombre · 36 · Cercano",
    thumbnail: `${AVATAR_CDN_BASE}/esteban.webp`,
    config: { gender: "male", age_range: "35-45", skin_tone: "light", hair: "short-straight", hair_color: "black", vibe: "professional", setting: "home-office" },
  },
  {
    id: "tomas",
    name: "Tomás",
    subtitle: "Hombre · 42 · Natural",
    thumbnail: `${AVATAR_CDN_BASE}/tomas.webp`,
    config: { gender: "male", age_range: "35-45", skin_tone: "light", hair: "short-straight", hair_color: "black", vibe: "casual-natural", setting: "kitchen" },
  },
  {
    id: "catalina",
    name: "Catalina",
    subtitle: "Mujer · 38 · Expresiva",
    thumbnail: `${AVATAR_CDN_BASE}/catalina.webp`,
    config: { gender: "female", age_range: "35-45", skin_tone: "light", hair: "long-wavy", hair_color: "red", vibe: "glam", setting: "minimal" },
  },
  {
    id: "avatar-31",
    name: "Mía",
    subtitle: "Mujer · 24 · Selfie",
    thumbnail: `${AVATAR_CDN_BASE}/avatar-31.webp`,
    config: { gender: "female", age_range: "18-25", skin_tone: "medium", hair: "long-straight", hair_color: "black", vibe: "casual-natural", setting: "outdoor" },
  },
  {
    id: "avatar-32",
    name: "Alana",
    subtitle: "Mujer · 25 · Natural",
    thumbnail: `${AVATAR_CDN_BASE}/avatar-32.webp`,
    config: { gender: "female", age_range: "25-35", skin_tone: "tan", hair: "long-wavy", hair_color: "brown", vibe: "casual-natural", setting: "bedroom" },
  },
];

type PresetAvatarRecommendation = {
  preset: PresetAvatar;
  score: number;
  recommended: boolean;
  reason: string;
};

function normalizeForMatching(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function includesAny(text: string, keywords: string[]) {
  return keywords.some((keyword) => text.includes(keyword));
}

function rankPresetAvatarsForProduct(
  presets: PresetAvatar[],
  productContext: string,
): PresetAvatarRecommendation[] {
  const text = normalizeForMatching(productContext);
  const signals = {
    supplement: includesAny(text, [
      "gumm",
      "fibra",
      "probiot",
      "prebiot",
      "suplement",
      "capsula",
      "vitamina",
      "colageno",
      "magnesio",
      "oregano",
      "carvacrol",
      "defensa",
      "digest",
      "intestinal",
      "inmun",
    ]),
    beautyBody: includesAny(text, [
      "masaje",
      "ventosa",
      "celulitis",
      "piel",
      "facial",
      "belleza",
      "estetica",
      "red light",
      "luz roja",
      "succion",
      "crema",
      "cabello",
      "biotina",
    ]),
    postureOffice: includesAny(text, [
      "postura",
      "espalda",
      "corrector",
      "ortoped",
      "oficina",
      "compu",
      "computador",
      "busto",
      "push",
      "brasier",
      "sosten",
    ]),
    tech: includesAny(text, [
      "hotspot",
      "modem",
      "wifi",
      "internet",
      "router",
      "usb",
      "lte",
      "4g",
      "tecnolog",
      "celular",
      "telefono",
      "phone",
    ]),
    kitchenHome: includesAny(text, [
      "olla",
      "cocina",
      "lonchera",
      "sarten",
      "hogar",
      "casa",
      "alimento",
      "comida",
      "mascota",
      "perro",
      "gato",
    ]),
    babyFamily: includesAny(text, [
      "bebe",
      "cuna",
      "nino",
      "nina",
      "mama",
      "papa",
      "familia",
      "crianza",
    ]),
  };

  return presets
    .map((preset, index) => {
      let score = 0;
      const reasons: string[] = [];
      const { config } = preset;

      if (signals.supplement) {
        if (config.setting === "kitchen" || config.setting === "bedroom") score += 3;
        if (config.vibe === "casual-natural" || config.vibe === "professional") score += 2;
        if (config.gender === "female") score += 1;
        if (config.gender === "male") score -= 4;
        if (preset.id === "valeria") score += 5;
        if (["paula", "sofia", "marcela"].includes(preset.id)) score += 4;
        if (["renata", "aura", "yuli", "alejandra", "karina"].includes(preset.id)) score += 2;
        if (config.age_range === "45+") score -= 2;
        reasons.push("wellness y consumo diario");
      }

      if (signals.beautyBody) {
        if (config.setting === "gym" || config.setting === "bathroom" || config.vibe === "sporty") score += 3;
        if (config.gender === "female") score += 1;
        if (["isabella", "juli", "renata", "daniela", "camila", "natalia", "ximena", "catalina"].includes(preset.id)) score += 3;
        reasons.push("belleza, cuerpo o demostración de uso");
      }

      if (signals.postureOffice) {
        if (config.setting === "home-office" || config.setting === "office" || config.setting === "bedroom") score += 3;
        if (config.vibe === "professional" || config.vibe === "casual-natural") score += 2;
        if (["mariana", "daniela", "laura", "lucia", "karina", "esteban", "fernando"].includes(preset.id)) score += 3;
        reasons.push("dolor de espalda, oficina o rutina diaria");
      }

      if (signals.tech) {
        if (config.setting === "office" || config.setting === "home-office") score += 3;
        if (config.gender === "male") score += 2;
        if (config.vibe === "professional" || config.vibe === "casual-natural") score += 1;
        if (["carlos", "andres", "rafael", "mateo", "diego", "fernando", "esteban", "tomas"].includes(preset.id)) score += 3;
        reasons.push("producto tech o solución práctica");
      }

      if (signals.kitchenHome && !signals.supplement) {
        if (config.setting === "kitchen") score += 4;
        if (["dona-lucia", "silvia", "clara", "valeria", "aura", "tomas", "marcela"].includes(preset.id)) score += 3;
        reasons.push("hogar, cocina o confianza familiar");
      }

      if (signals.babyFamily) {
        if (config.age_range === "25-35" || config.age_range === "35-45") score += 2;
        if (config.vibe === "casual-natural" || config.vibe === "professional") score += 2;
        if (["renata", "clara", "diego", "daniela", "laura", "alejandra", "esteban"].includes(preset.id)) score += 3;
        reasons.push("familia, cuidado o rutina de padres");
      }

      if (!Object.values(signals).some(Boolean)) {
        if (config.vibe === "casual-natural") score += 2;
        if (config.age_range === "25-35") score += 1;
        if (["valentina", "paula", "laura", "andres", "alejandra", "esteban"].includes(preset.id)) score += 1;
        reasons.push("perfil UGC versátil");
      }

      return {
        preset,
        score,
        recommended: score >= 5,
        reason: reasons[0] ?? "perfil UGC versátil",
        index,
      };
    })
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((item) => ({
      preset: item.preset,
      score: item.score,
      recommended: item.recommended,
      reason: item.reason,
    }));
}

// Phase 2 V2 — starting config for the custom builder. Deliberately empty
// so we don't render a "default portrait" the user never asked for. The
// preview surface shows an empty state until every field is filled, at
// which point the user clicks "Generar preview" to fire the call.
//
// Kept as a typed const (not null) because Partial<UGCAvatarConfig> flows
// more cleanly through the rest of the wizard; the `customComplete` gate
// ensures we never call the backend with an incomplete config.
const CUSTOM_EMPTY: Partial<UGCAvatarConfig> = {};

// Fallback used ONLY when bridging a saved avatar with unknown legacy keys
// into the full UGCAvatarConfig type expected by the sidebar. Never shown
// to the user.
const CUSTOM_FALLBACK: UGCAvatarConfig = {
  gender: "female",
  age_range: "25-35",
  skin_tone: "medium",
  hair: "long-wavy",
  hair_color: "black",
  vibe: "casual-natural",
  setting: "bedroom",
};

export interface AvatarCommit {
  config: UGCAvatarConfig;
  portraitUrl: string | null;
  source: "preset" | "custom" | "upload" | "prompt" | "recommended";
  presetId?: string;
  displayName?: string;
}

interface AvatarWizardModalProps {
  productId?: string | null;
  websiteId?: string | null;
  productName?: string;
  productDescription?: string | null;
  productImageUrl?: string | null;
  selectedAngle?: {
    name?: string | null;
    description?: string | null;
  } | null;
  onBack: () => void;
  onClose: () => void;
  onCommit: (avatar: AvatarCommit) => void;
}

type WizardMode = "choose" | "mine" | "preset" | "custom" | "prompt" | "upload";
type AvatarLibraryFilter = "all" | "female" | "male" | "younger" | "older" | "favorites";

const USER_AVATAR_UPLOAD_FOLDER = "dev/assets/avatars/uploads";
const AVATAR_LIBRARY_FILTERS: Array<{ id: AvatarLibraryFilter; label: string }> = [
  { id: "all", label: "Todos" },
  { id: "female", label: "Mujeres" },
  { id: "male", label: "Hombres" },
  { id: "younger", label: "Más jóvenes" },
  { id: "older", label: "Más adultos" },
  { id: "favorites", label: "Favoritos" },
];

export function AvatarWizardModal({
  productId,
  websiteId,
  productName,
  productDescription,
  productImageUrl,
  selectedAngle,
  onBack,
  onClose,
  onCommit,
}: AvatarWizardModalProps) {
  const { language } = useAuth();
  const t = (key: string, fallback?: string) =>
    dictionary[key]?.[language as Language] ?? fallback ?? key;

  // Open directly in the avatar library. The previous "choose between three
  // paths" screen added one extra click after "Configurar avatar"; users now
  // land on the ready-avatar catalog first, with bridges to custom/upload.
  const [mode, setMode] = useState<WizardMode>("preset");
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);

  // Phase 2 V2 — Fase C. "Mis avatares" library (persisted per user).
  const { avatars: myAvatars, save: saveAvatar, remove: removeAvatar, isLoading: myAvatarsLoading } =
    useMyAvatars(true);
  // Transient busy state for "Guardar en mi elenco" so the button can
  // show a spinner without freezing the rest of the wizard.
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [justSavedId, setJustSavedId] = useState<number | null>(null);
  const [savingPresetId, setSavingPresetId] = useState<string | null>(null);
  const [savedPresetIds, setSavedPresetIds] = useState<Set<string>>(() => new Set());
  const [libraryFilter, setLibraryFilter] = useState<AvatarLibraryFilter>("all");
  // Track which saved avatar is currently highlighted in `mine` mode.
  const [selectedMineId, setSelectedMineId] = useState<number | null>(null);
  const productContextText = `${productName ?? ""} ${productDescription ?? ""}`;
  const avatarRecommendations = useMemo(
    () => rankPresetAvatarsForProduct(PRESETS, productContextText),
    [productContextText],
  );
  const recommendedAvatarItems = useMemo(
    () => avatarRecommendations.slice(0, AVATAR_RECOMMENDATION_ROLES.length),
    [avatarRecommendations],
  );
  const recommendedPresetIds = useMemo(
    () => new Set(recommendedAvatarItems.map((item) => item.preset.id)),
    [recommendedAvatarItems],
  );
  const orderedPresets = useMemo(
    () => avatarRecommendations.map((item) => item.preset),
    [avatarRecommendations],
  );
  const filteredOrderedPresets = useMemo(() => {
    return orderedPresets.filter((preset) => {
      if (libraryFilter === "female") return preset.config.gender === "female";
      if (libraryFilter === "male") return preset.config.gender === "male";
      if (libraryFilter === "younger") return preset.config.age_range === "18-25" || preset.config.age_range === "25-35";
      if (libraryFilter === "older") return preset.config.age_range === "35-45" || preset.config.age_range === "45+";
      if (libraryFilter === "favorites") return savedPresetIds.has(preset.id);
      return true;
    });
  }, [libraryFilter, orderedPresets, savedPresetIds]);
  const [generatedRecommendations, setGeneratedRecommendations] = useState<AvatarRecommendationItem[]>([]);
  const [generatedRecommendationsLoading, setGeneratedRecommendationsLoading] = useState(false);
  const [generatedRecommendationsError, setGeneratedRecommendationsError] = useState(false);
  const [avatarRecommendationsRetryNonce, setAvatarRecommendationsRetryNonce] = useState(0);
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<string | null>(null);
  const [savingRecommendationId, setSavingRecommendationId] = useState<string | null>(null);
  const [savedRecommendationIds, setSavedRecommendationIds] = useState<Set<string>>(() => new Set());
  const avatarRecommendationReqRef = useRef(0);
  const avatarRecommendationInFlightKeyRef = useRef<string | null>(null);
  const avatarRecommendationRunRef = useRef<{ baseKey: string; runId: string } | null>(null);
  const avatarRecommendationContextKeyRef = useRef<string | null>(null);
  const avatarRecommendationLastForcedNonceRef = useRef(0);
  const usableGeneratedRecommendations = useMemo(
    () => generatedRecommendations.filter((item) => isUsableAvatarPreviewUrl(item.preview_url)),
    [generatedRecommendations],
  );
  const generatedRecommendationByRole = useMemo(
    () => new Map(usableGeneratedRecommendations.map((item) => [item.role, item])),
    [usableGeneratedRecommendations],
  );
  const showPresetRecommendationBadges =
    usableGeneratedRecommendations.length === 0 && !generatedRecommendationsLoading && !generatedRecommendationsError;

  useEffect(() => {
    if (mode !== "preset" || !productId) {
      return;
    }

    const contextPayload = {
      product_id: productId,
      website_id: websiteId ?? null,
      style_id: "product-modeling-voiceover",
      language: "es",
      sale_angle: selectedAngle
        ? {
            name: selectedAngle.name ?? "",
            description: selectedAngle.description ?? selectedAngle.name ?? "",
          }
        : null,
    };
    let effectiveRetryNonce = avatarRecommendationsRetryNonce;
    const contextKey = JSON.stringify(contextPayload);
    if (avatarRecommendationContextKeyRef.current !== contextKey) {
      avatarRecommendationContextKeyRef.current = contextKey;
      avatarRecommendationLastForcedNonceRef.current = 0;
      effectiveRetryNonce = 0;
      if (avatarRecommendationsRetryNonce !== 0) {
        setAvatarRecommendationsRetryNonce(0);
      }
    }
    const shouldForceRegenerate =
      effectiveRetryNonce > 0 && avatarRecommendationLastForcedNonceRef.current !== effectiveRetryNonce;
    if (shouldForceRegenerate) {
      avatarRecommendationLastForcedNonceRef.current = effectiveRetryNonce;
    }

    const basePayload = {
      ...contextPayload,
      force_regenerate: shouldForceRegenerate,
    };
    const baseKey = JSON.stringify({
      ...basePayload,
      avatarRecommendationsRetryNonce: shouldForceRegenerate ? effectiveRetryNonce : 0,
    });
    if (avatarRecommendationRunRef.current?.baseKey !== baseKey) {
      avatarRecommendationRunRef.current = {
        baseKey,
        runId: createAvatarRecommendationRunId(),
      };
    }

    const payload = {
      ...basePayload,
      client_generation_id: avatarRecommendationRunRef.current.runId,
    };
    const requestKey = JSON.stringify(payload);
    if (avatarRecommendationInFlightKeyRef.current === requestKey) {
      return;
    }

    const reqId = ++avatarRecommendationReqRef.current;
    avatarRecommendationInFlightKeyRef.current = requestKey;
    setGeneratedRecommendations([]);
    setSelectedRecommendationId(null);
    setGeneratedRecommendationsLoading(true);
    setGeneratedRecommendationsError(false);

    (async () => {
      const settled = await Promise.all(
        AVATAR_RECOMMENDATION_ROLES.map(async (role) => {
          for (let attempt = 0; attempt < AVATAR_RECOMMENDATION_MAX_ATTEMPTS; attempt += 1) {
            try {
              const response = await getAvatarRecommendationRole(role, payload);
              const recommendation = response.res;
              if (!response.error && recommendation && isUsableAvatarPreviewUrl(recommendation.preview_url)) {
                if (reqId === avatarRecommendationReqRef.current) {
                  setGeneratedRecommendations((current) => {
                    const next = new Map(current.map((item) => [item.role, item]));
                    next.set(role, recommendation);
                    return AVATAR_RECOMMENDATION_ROLES
                      .map((orderedRole) => next.get(orderedRole))
                      .filter(Boolean) as AvatarRecommendationItem[];
                  });
                }
                return recommendation;
              }
            } catch {
              // Retry below; partial cards should still appear as their roles finish.
            }

            if (attempt < AVATAR_RECOMMENDATION_MAX_ATTEMPTS - 1) {
              await wait(900 + attempt * 700);
            }
          }
          return null;
        }),
      );

      if (reqId !== avatarRecommendationReqRef.current) return;

      const ordered = AVATAR_RECOMMENDATION_ROLES
        .map((role) => settled.find((item) => item?.role === role))
        .filter(Boolean) as AvatarRecommendationItem[];

      avatarRecommendationInFlightKeyRef.current = null;
      setGeneratedRecommendationsLoading(false);
      setGeneratedRecommendationsError(ordered.length === 0);
    })();
  }, [mode, productId, selectedAngle, websiteId, avatarRecommendationsRetryNonce]);

  // Custom mode state — starts empty. The user picks every axis before
  // we burn a Gemini call; "Generar preview" is an explicit action.
  const [customConfig, setCustomConfig] = useState<Partial<UGCAvatarConfig>>(CUSTOM_EMPTY);
  const [customPreviewUrl, setCustomPreviewUrl] = useState<string | null>(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState(false);
  // Abort controller so a stale preview never overwrites a fresher one.
  const reqSeqRef = useRef(0);

  const regenerateCustomPreview = useCallback(
    async (config: UGCAvatarConfig) => {
      setCustomLoading(true);
      setCustomError(false);
      const mySeq = ++reqSeqRef.current;
      const { res, error } = await previewAvatar({
        avatar_config: config,
        product_image_url: productImageUrl ?? null,
        product_name: productName ?? null,
      });
      if (mySeq !== reqSeqRef.current) return;
      if (error || !res) {
        setCustomError(true);
        setCustomLoading(false);
        return;
      }
      setCustomPreviewUrl(res.preview_url);
      setCustomLoading(false);
    },
    [productImageUrl, productName]
  );

  // Reset the preview whenever the user changes anything — prevents a
  // stale portrait from sticking around after they edited the config.
  // The next preview only fires when they click "Generar preview".
  const updateCustom = (patch: Partial<UGCAvatarConfig>) => {
    setCustomConfig((prev) => ({ ...prev, ...patch }));
    setCustomPreviewUrl(null);
    setCustomError(false);
  };

  const customComplete = useMemo(
    () =>
      !!customConfig.gender &&
      !!customConfig.age_range &&
      !!customConfig.skin_tone &&
      !!customConfig.hair &&
      !!customConfig.hair_color &&
      !!customConfig.vibe &&
      !!customConfig.setting,
    [customConfig]
  );

  // Count how many axes are filled so the user can see progress (e.g.
  // "4 / 7") before the "Generar preview" CTA unlocks.
  const customFilledCount = useMemo(() => {
    return [
      customConfig.gender,
      customConfig.age_range,
      customConfig.skin_tone,
      customConfig.hair,
      customConfig.hair_color,
      customConfig.vibe,
      customConfig.setting,
    ].filter(Boolean).length;
  }, [customConfig]);

  // Phase 2 V2 Fase 3 — free-form prompt state.
  // Separate from custom mode so the user can freely switch between the
  // structured builder and a pasted detailed description without losing
  // what they wrote in either side.
  const [promptText, setPromptText] = useState("");
  const [promptPreviewUrl, setPromptPreviewUrl] = useState<string | null>(null);
  const [promptPreviewSourceText, setPromptPreviewSourceText] = useState("");
  const [promptPreviewCustomPrompt, setPromptPreviewCustomPrompt] = useState("");
  const [promptRegenerationOpen, setPromptRegenerationOpen] = useState(false);
  const [promptRegenerationNote, setPromptRegenerationNote] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState(false);
  const promptReqSeqRef = useRef(0);

  // Upload mode — user's own avatar photo. The file is uploaded to the same
  // S3 service used by the builder, then it can be saved into "Mis avatares"
  // or used immediately as the committed presenter reference.
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null);
  const [uploadS3Url, setUploadS3Url] = useState<string | null>(null);
  const [uploadDisplayName, setUploadDisplayName] = useState(
    t("avatar-wizard-upload-default-name", "Mi avatar")
  );
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const promptValid = promptText.trim().length > 0;
  const promptNeedsRefresh =
    !!promptPreviewUrl && promptPreviewSourceText.trim() !== promptText.trim();

  const regeneratePromptPreview = useCallback(
    async (prompt: string, regenerationNote: string = "") => {
      setPromptLoading(true);
      setPromptError(false);
      const mySeq = ++promptReqSeqRef.current;
      const basePrompt = prompt.trim();
      const extraInstruction = regenerationNote.trim();
      const finalPrompt = extraInstruction
        ? `${basePrompt}\n\nRegeneration instruction from the user: ${extraInstruction}`
        : basePrompt;
      // Ride the same /avatar/preview endpoint: when the backend receives
      // avatar_config.custom_prompt, the expander short-circuits and uses
      // the text verbatim. No new endpoint needed.
      const { res, error } = await previewAvatar({
        avatar_config: { custom_prompt: finalPrompt } as UGCAvatarConfig,
        product_image_url: productImageUrl ?? null,
        product_name: productName ?? null,
      });
      if (mySeq !== promptReqSeqRef.current) return;
      if (error || !res) {
        setPromptError(true);
        setPromptLoading(false);
        return;
      }
      setPromptPreviewUrl(res.preview_url);
      setPromptPreviewSourceText(basePrompt);
      setPromptPreviewCustomPrompt(finalPrompt);
      setPromptRegenerationOpen(false);
      setPromptRegenerationNote("");
      setPromptLoading(false);
    },
    [productImageUrl, productName]
  );

  const commitPreset = async (preset: PresetAvatar) => {
    let portraitUrl: string | null = preset.thumbnail;

    // Preset avatars now live on a public HTTPS CDN. Keep that URL as the
    // identity reference instead of inflating it into a data URI; image
    // providers preserve identity better from a stable URL and payloads stay
    // small enough for OpenAI/Gemini edit calls. Only old relative presets
    // need conversion.
    if (!preset.thumbnail.startsWith("https://") && !preset.thumbnail.startsWith("data:")) {
      try {
        const response = await fetch(preset.thumbnail);
        if (response.ok) {
          const blob = await response.blob();
          const dataUri = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          portraitUrl = dataUri;
        }
      } catch (err) {
        console.warn("[AvatarWizard] preset→dataURI conversion failed:", err);
      }
    }
    onCommit({
      config: preset.config,
      portraitUrl,
      source: "preset",
      presetId: preset.id,
      displayName: preset.name,
    });
  };

  const commitCustom = () => {
    if (!customComplete) return;
    // customComplete guarantees every axis is filled — safe cast to the
    // full UGCAvatarConfig type here.
    onCommit({
      config: customConfig as UGCAvatarConfig,
      portraitUrl: customPreviewUrl,
      source: "custom",
      displayName: t("avatar-wizard-custom-name", "Avatar personalizado"),
    });
  };

  const commitMine = (a: ParsedSavedAvatar) => {
    // Saved rows may have partial avatar_config if the schema ever widens.
    // Fall back to CUSTOM_FALLBACK for any missing field so the sidebar +
    // director always receive a complete config.
    const fullConfig: UGCAvatarConfig = {
      ...CUSTOM_FALLBACK,
      ...a.parsedConfig,
    };
    onCommit({
      config: fullConfig,
      portraitUrl: a.thumbnail_url,
      source: (a.source as "preset" | "custom" | "upload" | "prompt" | "recommended") ?? "custom",
      displayName: a.display_name,
    });
  };

  const buildRecommendationConfig = (recommendation: AvatarRecommendationItem): UGCAvatarConfig => ({
    ...CUSTOM_FALLBACK,
    ...(recommendation.avatar_config as Partial<UGCAvatarConfig>),
    recommendation_id: recommendation.id,
    recommendation_role: recommendation.role,
    recommendation_label: getAvatarCreativeTitle(recommendation.role),
    recommendation_fit_reason: recommendation.fit_reason,
    recommendation_preview_url: recommendation.preview_url,
    image_provider: recommendation.provider,
    image_model: recommendation.model,
    source: "ai_recommended",
  });

  const commitRecommendation = (recommendation: AvatarRecommendationItem) => {
    onCommit({
      config: buildRecommendationConfig(recommendation),
      portraitUrl: recommendation.preview_url,
      source: "recommended",
      presetId: recommendation.id,
      displayName: getAvatarCreativeTitle(recommendation.role),
    });
  };

  // Phase 2 V2 Fase 3 — commit the free-form prompt avatar. The AvatarCommit
  // `config` still carries the 7 required fields (filled from the fallback)
  // so upstream type contracts hold; the custom_prompt field is what the
  // backend actually consumes when generating the video.
  const commitPrompt = () => {
    if (!promptValid || !promptPreviewUrl) return;
    onCommit({
      config: {
        ...CUSTOM_FALLBACK,
        custom_prompt: promptPreviewCustomPrompt || promptText.trim(),
      },
      portraitUrl: promptPreviewUrl,
      source: "prompt",
      displayName: t("avatar-wizard-prompt-name", "Avatar desde descripción"),
    });
  };

  const commitUpload = () => {
    if (!uploadS3Url) return;
    onCommit({
      config: {
        ...CUSTOM_FALLBACK,
        custom_prompt:
          "User uploaded avatar reference image. Preserve the identity, face, hair, skin tone, outfit vibe, and real-photo look from the uploaded image.",
      },
      portraitUrl: uploadS3Url,
      source: "upload",
      displayName: uploadDisplayName.trim() || t("avatar-wizard-upload-default-name", "Mi avatar"),
    });
  };

  // Phase 2 V2 — Fase C. Save the current preset / custom build into the
  // user's cast. We do NOT auto-commit the draft on save — the user may
  // want to save without starting a video right away. Success is indicated
  // by a brief ✓ state on the button.
  const handleSavePreset = async (preset: PresetAvatar) => {
    if (savingAvatar || savingPresetId) return;
    setSavingAvatar(true);
    setSavingPresetId(preset.id);
    const saved = await saveAvatar({
      display_name: preset.name,
      thumbnail_url: preset.thumbnail,
      avatar_config: preset.config,
      source: "preset",
    });
    setSavingAvatar(false);
    setSavingPresetId(null);
    if (saved) {
      setJustSavedId(saved.id);
      setSavedPresetIds((prev) => new Set(prev).add(preset.id));
      setTimeout(() => setJustSavedId(null), 2000);
    }
  };

  const handleSaveRecommendation = async (recommendation: AvatarRecommendationItem) => {
    if (savingAvatar || savingRecommendationId || savedRecommendationIds.has(recommendation.id)) return;
    setSavingAvatar(true);
    setSavingRecommendationId(recommendation.id);
    const saved = await saveAvatar({
      display_name: getAvatarCreativeTitle(recommendation.role),
      thumbnail_url: recommendation.preview_url,
      avatar_config: buildRecommendationConfig(recommendation),
      source: "recommended",
    });
    setSavingAvatar(false);
    setSavingRecommendationId(null);
    if (saved) {
      setJustSavedId(saved.id);
      setSavedRecommendationIds((prev) => new Set(prev).add(recommendation.id));
      setTimeout(() => setJustSavedId(null), 2000);
    }
  };

  const handleSaveCustom = async () => {
    if (savingAvatar || !customComplete || !customPreviewUrl) return;
    setSavingAvatar(true);
    // customComplete guarantees every axis is filled before saving.
    const saved = await saveAvatar({
      display_name: t("avatar-wizard-custom-name", "Avatar personalizado"),
      thumbnail_url: customPreviewUrl,
      avatar_config: customConfig as UGCAvatarConfig,
      source: "custom",
    });
    setSavingAvatar(false);
    if (saved) {
      setJustSavedId(saved.id);
      setTimeout(() => setJustSavedId(null), 2000);
    }
  };

  const handleDeleteMine = async (id: number) => {
    await removeAvatar(id);
    if (selectedMineId === id) setSelectedMineId(null);
  };

  // Phase 2 V2 Fase 3 — save a prompt-based avatar. Stores the raw prompt
  // inside avatar_config.custom_prompt so re-using it later triggers the
  // same short-circuit path in the backend expander.
  const handleSavePrompt = async () => {
    if (savingAvatar || !promptValid || !promptPreviewUrl) return;
    setSavingAvatar(true);
    const saved = await saveAvatar({
      display_name: t("avatar-wizard-prompt-name", "Avatar desde descripción"),
      thumbnail_url: promptPreviewUrl,
      avatar_config: { ...CUSTOM_FALLBACK, custom_prompt: promptPreviewCustomPrompt || promptText.trim() },
      source: "prompt",
    });
    setSavingAvatar(false);
    if (saved) {
      setJustSavedId(saved.id);
      setTimeout(() => setJustSavedId(null), 2000);
    }
  };

  const handleUploadFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setUploadError(t("avatar-wizard-upload-error-type", "Sube una imagen JPG, PNG o WebP."));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError(t("avatar-wizard-upload-error-size", "La imagen debe pesar menos de 10 MB."));
      return;
    }

    setUploadLoading(true);
    setUploadError(null);
    setUploadS3Url(null);
    setUploadFileName(file.name);

    try {
      const dataUri = await toBase64(file);
      setUploadPreviewUrl(dataUri);
      const safeName = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9.]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const response = await uploadImagesToS3({
        file: dataUri,
        folder: USER_AVATAR_UPLOAD_FOLDER,
        filename: `avatar-${Date.now()}-${safeName || "upload.webp"}`,
      });
      const s3Url = response?.s3_url;
      if (!s3Url) throw new Error("missing s3_url");
      setUploadS3Url(s3Url);
      setUploadPreviewUrl(s3Url);
      const baseName = file.name.replace(/\.[^.]+$/, "").trim();
      if (baseName && uploadDisplayName === t("avatar-wizard-upload-default-name", "Mi avatar")) {
        setUploadDisplayName(baseName);
      }
    } catch (err) {
      console.warn("[AvatarWizard] avatar upload failed:", err);
      setUploadError(t("avatar-wizard-upload-error-generic", "No pudimos subir la imagen. Intenta de nuevo."));
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSaveUpload = async () => {
    if (savingAvatar || !uploadS3Url) return;
    setSavingAvatar(true);
    const saved = await saveAvatar({
      display_name: uploadDisplayName.trim() || t("avatar-wizard-upload-default-name", "Mi avatar"),
      thumbnail_url: uploadS3Url,
      avatar_config: {
        ...CUSTOM_FALLBACK,
        custom_prompt:
          "User uploaded avatar reference image. Preserve the identity, face, hair, skin tone, outfit vibe, and real-photo look from the uploaded image.",
      },
      source: "upload",
    });
    setSavingAvatar(false);
    if (saved) {
      setJustSavedId(saved.id);
      setSelectedMineId(saved.id);
      setTimeout(() => setJustSavedId(null), 2000);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="relative flex h-full max-h-full flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-indigo-50/50 via-white to-white px-6 py-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={() => {
              if (mode === "preset" || mode === "choose") {
                onBack();
                return;
              }
              setMode("preset");
            }}
            className="mr-0.5 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/80 hover:text-foreground"
            aria-label={t("video-preview-back-to-studio", "Volver")}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-indigo-200/80">
            {productImageUrl ? (
              <img
                src={productImageUrl}
                alt={productName ?? t("avatar-wizard-product-alt", "Producto")}
                className="h-full w-full object-contain p-0.5"
              />
            ) : (
              <div className="h-full w-full bg-muted/30" />
            )}
          </div>
          <div className="min-w-0">
            <h2 className="max-w-[280px] truncate text-base font-semibold leading-tight text-foreground">
              {shortProductName(productName)}
            </h2>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/80 hover:text-foreground"
          aria-label={t("close", "Cerrar")}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <>
        {mode === "choose" && (
          <motion.div
            key="choose"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 overflow-y-auto p-6 md:p-8 flex items-center justify-center"
          >
            {/* Phase 6 V3d (Apr 20 2026) — Claude Design handoff FINAL layout
                (per user screenshot 2026-04-20 1:55:27). 3 large cards:
                ・ Card 1 (BLUE→PINK) "Biblioteca de presenters" — fan of 5
                  animated portraits, badge "Recomendado" pulsante.
                ・ Card 2 (INDIGO) "A tu medida, con IA" — shows 2 sub-modes
                  preview: parameter pills AND freeform prompt textarea.
                  Badge "Personalizable".
                ・ Card 3 (PURPLE) "Usa tu propia foto" — drop zone placeholder.
                The ORANGE "Mis avatares" card from check-s4.png was an earlier
                iteration; user removed it. Access to saved avatars lives
                elsewhere (TBD — possibly a small CTA or moved to preset
                mode header). */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-5xl mx-auto">
              {/* ─── Card 1 — Biblioteca de presenters (BLUE→PINK) ─── */}
              <AvatarWizardChoiceCard
                variant="blue"
                eyebrow={t("avatar-wizard-eyebrow-presenter", "AVATARES LISTOS")}
                title={t("avatar-wizard-choice-presenter-title-v3a", "Elige un rostro")}
                badge={
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur-md"
                        style={{ background: "rgba(67,104,229,.12)", color: "#4368E5", border: "1px solid rgba(67,104,229,.2)" }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                    {t("avatar-wizard-choice-fast-badge", "Rápido")}
                  </span>
                }
                cta={t("avatar-wizard-choice-unified-cta", "Ver opciones")}
                ctaColor="#4368E5"
                caption={t("avatar-wizard-choice-presenter-caption", "8 avatares listos para grabar")}
                onClick={() => setMode("preset")}
                visual={<PresentersFanPeek presets={PRESETS.slice(0, 5)} />}
              />

              {/* ─── Card 2 — A tu medida, con IA (INDIGO) ─── */}
              <AvatarWizardChoiceCard
                variant="indigo"
                eyebrow={t("avatar-wizard-eyebrow-build", "CREA UNO")}
                title={t("avatar-wizard-choice-build-title-v3a", "Personalízalo")}
                badge={
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur-md"
                        style={{ background: "rgba(99,102,241,.12)", color: "#6366F1", border: "1px solid rgba(99,102,241,.2)" }}>
                    <Sparkles className="h-3 w-3" />
                    {t("avatar-wizard-choice-build-badge", "IA")}
                  </span>
                }
                cta={t("avatar-wizard-choice-build-cta", "Crear avatar")}
                ctaColor="#6366F1"
                caption={t("avatar-wizard-choice-build-caption", "Define rasgos o descríbelo con prompt")}
                onClick={() => {
                  setMode("custom");
                  setCustomPreviewUrl(null);
                  setCustomError(false);
                }}
                visual={<ConstruyeAvatarAIPeek />}
              />

              {/* ─── Card 3 — Usa tu propia foto (PURPLE) ─── */}
              <AvatarWizardChoiceCard
                variant="purple"
                eyebrow={t("avatar-wizard-eyebrow-upload", "TU FOTO")}
                title={t("avatar-wizard-choice-upload-title-v3a", "Foto propia")}
                badge={
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap backdrop-blur-md"
                        style={{ background: "rgba(168,85,247,.12)", color: "#9333EA", border: "1px solid rgba(168,85,247,.2)" }}>
                    <Upload className="h-3 w-3" />
                    {t("avatar-wizard-choice-upload-badge", "Pronto")}
                  </span>
                }
                cta={t("avatar-wizard-choice-upload-cta", "Ver estado")}
                ctaColor="#9333EA"
                caption={t("avatar-wizard-choice-upload-caption", "Tu imagen como referencia")}
                onClick={() => setMode("upload")}
                visual={<UploadDropZonePeek />}
              />
            </div>
          </motion.div>
        )}

        {mode === "mine" && (
          <motion.div
            key="mine"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="flex-1 overflow-y-auto p-5 md:p-6"
          >
            {myAvatarsLoading && myAvatars.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : myAvatars.length === 0 ? (
              <div className="max-w-md mx-auto text-center py-16 space-y-3">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-muted flex items-center justify-center">
                  <UserCircle2 className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-base font-semibold text-foreground">
                  {t("avatar-wizard-mine-empty-title", "Todavía no guardaste avatares")}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t(
                    "avatar-wizard-mine-empty-desc-v2",
                    "Elige un preset de Fluxi o personaliza los detalles — se guarda en tu elenco y lo reutilizas en todos tus videos."
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => setMode("preset")}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90"
                >
                  {t("avatar-wizard-choice-unified-title", "Elegir o personalizar")}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-5xl mx-auto">
                {myAvatars.map((a) => {
                  const isSelected = selectedMineId === a.id;
                  return (
                    <div key={a.id} className="relative group">
                      <button
                        type="button"
                        onClick={() => setSelectedMineId(a.id)}
                        className={cn(
                          "w-full rounded-2xl overflow-hidden text-left transition-all",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                          isSelected
                            ? "ring-2 ring-primary ring-offset-2 shadow-lg scale-[1.02]"
                            : "hover:scale-[1.02] shadow-sm hover:shadow-md"
                        )}
                      >
                        <div className="aspect-[3/4] relative bg-muted/30">
                          <AvatarThumbnailImage src={a.thumbnail_url} alt={a.display_name} />
                          {isSelected && (
                            <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                              <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pt-8 pb-3 px-3">
                            <p className="text-sm font-bold text-white leading-tight truncate">
                              {a.display_name}
                            </p>
                            <p className="text-[11px] text-white/85 leading-tight uppercase tracking-wide">
                              {a.source === "preset"
                                ? t("avatar-wizard-source-preset", "Preset")
                                : a.source === "upload"
                                ? t("avatar-wizard-source-upload", "Foto")
                                : t("avatar-wizard-source-custom", "Custom")}
                            </p>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMine(a.id);
                        }}
                        aria-label={t("avatar-wizard-mine-delete", "Eliminar del elenco")}
                        className="absolute top-2 left-2 h-7 w-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500 transition"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {mode === "preset" && (
          <motion.div
            key="preset"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="flex-1 overflow-y-auto bg-slate-50/70 px-5 pb-4 pt-3 md:px-8"
          >
            <div className="mx-auto mb-3 flex max-w-[756px] flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-xl font-semibold text-slate-950">
                {t("avatar-wizard-preset-title-short", "Elige un avatar")}
              </h3>
              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setMode("prompt");
                    setPromptPreviewUrl(null);
                    setPromptError(false);
                  }}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("avatar-wizard-library-create-prompt-title", "Crear con IA")}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("upload")}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {t("avatar-wizard-upload-tab-title", "Subir avatar")}
                </button>
              </div>
            </div>

            {(generatedRecommendationsLoading || usableGeneratedRecommendations.length > 0 || generatedRecommendationsError) && (
              <div className="mx-auto mb-3 max-w-[756px]">
                {generatedRecommendationsError ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:[grid-template-columns:repeat(4,180px)]">
                    {AVATAR_RECOMMENDATION_LOADING_CARDS.map((item) => (
                      <div
                        key={item.title}
                        className="relative aspect-[9/16] w-full max-w-[180px] overflow-hidden rounded-xl border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-indigo-50 shadow-sm"
                      >
                        <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
                          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-amber-500 shadow-[0_12px_34px_rgba(245,158,11,0.18)] ring-1 ring-amber-100">
                            <RefreshCw className="h-6 w-6" />
                          </div>
                          <p className="text-[13px] font-semibold text-gray-900">{item.title}</p>
                          <p className="mt-1 text-[11px] leading-snug text-gray-500">
                            {t("avatar-wizard-ai-recommendations-card-error", "No se generó esta opción.")}
                          </p>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setGeneratedRecommendationsError(false);
                        setAvatarRecommendationsRetryNonce((value) => value + 1);
                      }}
                      className="col-span-1 inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-indigo-100 bg-white px-4 text-[13px] font-semibold text-indigo-600 shadow-sm transition hover:bg-indigo-50 sm:col-span-4"
                    >
                      <RefreshCw className="h-4 w-4" />
                      {t("avatar-wizard-ai-recommendations-retry", "Volver a crear recomendaciones")}
                    </button>
                  </div>
                ) : generatedRecommendationsLoading ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:[grid-template-columns:repeat(4,180px)]">
                    {AVATAR_RECOMMENDATION_LOADING_CARDS.map((item, index) => {
                      const role = AVATAR_RECOMMENDATION_ROLES[index] ?? AVATAR_RECOMMENDATION_ROLES[0];
                      const recommendation = generatedRecommendationByRole.get(role);
                      if (recommendation && isUsableAvatarPreviewUrl(recommendation.preview_url)) {
                        const isSelected = selectedRecommendationId === recommendation.id;
                        return (
                          <div
                            role="button"
                            tabIndex={0}
                            key={recommendation.id}
                            onClick={() => {
                              setSelectedRecommendationId(recommendation.id);
                              setSelectedPresetId(null);
                            }}
                            className={cn(
                              "group relative aspect-[9/16] w-full max-w-[180px] cursor-pointer overflow-hidden rounded-xl text-left transition-all",
                              isSelected ? "ring-2 ring-indigo-500 ring-offset-2" : "ring-2 ring-indigo-200 shadow-sm"
                            )}
                          >
                            <AvatarThumbnailImage
                              src={recommendation.preview_url}
                              alt={recommendation.display_name}
                              imageClassName={isSelected ? "scale-105" : "group-hover:scale-105"}
                            />
                            {isSelected && (
                              <div className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-white shadow-md">
                                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                              </div>
                            )}
                            <div
                              className="absolute inset-x-0 bottom-0 p-3 opacity-100"
                              style={{
                                background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.88) 100%)",
                              }}
                            >
                              <p className="text-[13px] font-semibold leading-tight text-white">
                                {getAvatarCreativeTitle(recommendation.role)}
                              </p>
                              <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-white/80">
                                {recommendation.fit_reason}
                              </p>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <AvatarRecommendationLoadingCard
                          key={item.title}
                          title={item.title}
                          index={index}
                        />
                      );
                    })}
                  </div>
                ) : usableGeneratedRecommendations.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:[grid-template-columns:repeat(4,180px)]">
                    {AVATAR_RECOMMENDATION_ROLES.map((role, index) => {
                      const recommendation = generatedRecommendationByRole.get(role);
                      if (!recommendation) {
                        return (
                          <button
                            key={role}
                            type="button"
                            onClick={() => {
                              setGeneratedRecommendationsError(false);
                              setAvatarRecommendationsRetryNonce((value) => value + 1);
                            }}
                            className="relative aspect-[9/16] w-full max-w-[180px] overflow-hidden rounded-xl border border-dashed border-slate-200 bg-white text-slate-500 transition hover:border-indigo-200 hover:bg-indigo-50/40"
                          >
                            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                              <RefreshCw className="h-5 w-5" />
                              <p className="mt-2 text-[12px] font-semibold">{getAvatarCreativeTitle(role)}</p>
                            </div>
                          </button>
                        );
                      }
                      const isSelected = selectedRecommendationId === recommendation.id;
                      const isSaving = savingRecommendationId === recommendation.id;
                      const isSaved = savedRecommendationIds.has(recommendation.id);
                      return (
                        <div
                          role="button"
                          tabIndex={0}
                          key={recommendation.id}
                          onClick={() => {
                            setSelectedRecommendationId(recommendation.id);
                            setSelectedPresetId(null);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedRecommendationId(recommendation.id);
                              setSelectedPresetId(null);
                            }
                          }}
                          className={cn(
                            "group relative aspect-[9/16] w-full max-w-[180px] cursor-pointer overflow-hidden rounded-xl text-left transition-all",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60",
                            isSelected
                              ? "ring-2 ring-indigo-500 ring-offset-2"
                              : index === 0
                                ? "ring-2 ring-indigo-200 shadow-[0_10px_30px_rgba(67,104,229,0.10)] hover:ring-indigo-300"
                                : "ring-1 ring-gray-200 hover:ring-2 hover:ring-indigo-300"
                          )}
                        >
                          <AvatarThumbnailImage
                            src={recommendation.preview_url}
                            alt={recommendation.display_name}
                            imageClassName={isSelected ? "scale-105" : "group-hover:scale-105"}
                          />

                          {isSelected && (
                            <div className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-white shadow-md">
                              <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            </div>
                          )}

                          <button
                            type="button"
                            disabled={isSaving || isSaved}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSaveRecommendation(recommendation);
                            }}
                            aria-label={t("avatar-wizard-save-to-cast", "Guardar en mi elenco")}
                            className={cn(
                              "absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-indigo-600 shadow-[0_6px_18px_rgba(15,23,42,0.18)] ring-1 ring-indigo-100 backdrop-blur-sm transition",
                              "hover:bg-indigo-50 disabled:cursor-default",
                              isSelected ? "translate-y-8" : "",
                              isSaved ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                            )}
                          >
                            {isSaving ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Star className={cn("h-3.5 w-3.5", isSaved && "fill-indigo-500")} strokeWidth={2.4} />
                            )}
                          </button>

                          <div
                            className={cn(
                              "absolute inset-x-0 bottom-0 p-3 transition-opacity",
                              isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                            )}
                            style={{
                              background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.88) 100%)",
                            }}
                          >
                            <p className="text-[13px] font-semibold leading-tight text-white">
                              {getAvatarCreativeTitle(recommendation.role)}
                            </p>
                            <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-white/80">
                              {recommendation.fit_reason}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}

            <div className="mx-auto mb-2 flex max-w-[756px] items-end justify-between gap-3 border-t border-gray-100 pt-3">
              <div>
                <p className="text-[14px] font-semibold text-gray-950 leading-tight">
                  {t("avatar-wizard-library-title", "Biblioteca de avatares")}
                </p>
              </div>
              <span className="hidden rounded-full bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-500 ring-1 ring-gray-200 sm:inline-flex">
                {PRESETS.length} avatares
              </span>
            </div>

            <div className="mx-auto mb-2 flex max-w-[756px] gap-2 overflow-x-auto py-1">
              {AVATAR_LIBRARY_FILTERS.map((filter) => {
                const active = libraryFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setLibraryFilter(filter.id)}
                    className={cn(
                      "h-8 shrink-0 rounded-full px-3 text-[12px] font-semibold transition",
                      active
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 hover:text-gray-900"
                    )}
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>

            <div className="mx-auto grid max-w-[756px] grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
              {filteredOrderedPresets.map((p) => {
                const isSelected = selectedPresetId === p.id;
                const isRecommended = showPresetRecommendationBadges && recommendedPresetIds.has(p.id);
                const isPresetSaving = savingPresetId === p.id;
                const isPresetSaved = savedPresetIds.has(p.id);
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={p.id}
                    onClick={() => {
                      setSelectedPresetId(p.id);
                      setSelectedRecommendationId(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedPresetId(p.id);
                        setSelectedRecommendationId(null);
                      }
                    }}
                    className={cn(
                      "group relative aspect-[9/16] cursor-pointer rounded-xl overflow-hidden text-left transition-all",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60",
                      isSelected
                        ? "ring-2 ring-indigo-500 ring-offset-2"
                        : isRecommended
                          ? "ring-2 ring-indigo-200 hover:ring-indigo-300 shadow-[0_10px_30px_rgba(67,104,229,0.10)]"
                          : "ring-1 ring-gray-200 hover:ring-2 hover:ring-indigo-300"
                    )}
                  >
                    <AvatarThumbnailImage
                      src={p.thumbnail}
                      alt={p.name}
                      imageClassName={isSelected ? "scale-105" : "group-hover:scale-105"}
                    />

                    {isSelected && (
                      <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-indigo-500 text-white flex items-center justify-center shadow-md z-10">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </div>
                    )}

                    <button
                      type="button"
                      disabled={isPresetSaving || isPresetSaved}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (isPresetSaved) return;
                        handleSavePreset(p);
                      }}
	                      aria-label={t("avatar-wizard-save-to-cast", "Guardar en mi elenco")}
	                      className={cn(
	                        "absolute right-2 top-2 z-20 h-7 w-7 rounded-full bg-white/95 text-indigo-600 shadow-[0_6px_18px_rgba(15,23,42,0.18)] ring-1 ring-indigo-100 backdrop-blur-sm transition",
	                        "flex items-center justify-center hover:bg-indigo-50 disabled:cursor-default",
	                        isSelected ? "translate-y-8" : "",
	                        isPresetSaved ? "opacity-100" : "opacity-0 group-hover:opacity-100"
	                      )}
	                    >
                      {isPresetSaving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Star
                          className={cn("h-3.5 w-3.5", isPresetSaved && "fill-indigo-500")}
                          strokeWidth={2.4}
                        />
                      )}
                    </button>

                    <div
                      className={cn(
                        "absolute inset-x-0 bottom-0 p-2.5 transition-opacity",
                        isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                      )}
                      style={{
                        background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)",
                      }}
                    >
                      <p className="text-white text-[13px] font-semibold leading-tight">{p.name}</p>
                      <p className="text-white/80 text-[10px] leading-tight mt-0.5">{p.subtitle}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {filteredOrderedPresets.length === 0 && (
              <div className="mx-auto flex max-w-6xl items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
                <p className="text-sm font-medium text-gray-500">
                  {libraryFilter === "favorites"
                    ? "Marca avatares con la estrella para verlos aquí."
                    : "No hay avatares en este filtro."}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {mode === "custom" && (
          <motion.div
            key="custom"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="flex-1 grid grid-cols-1 md:grid-cols-[minmax(0,0.52fr)_minmax(0,0.48fr)] overflow-hidden"
          >
            {/* Live preview — stays empty until the user completes every
                field and explicitly clicks "Generar preview". No more
                auto-debounce burning Gemini credits on partial input. */}
            <div className="bg-gradient-to-br from-muted/30 to-muted/10 flex items-center justify-center p-7 border-r border-border/60">
              <div className="relative aspect-[9/16] w-full max-w-[340px] rounded-3xl overflow-hidden bg-muted/50 shadow-xl">
                {customPreviewUrl && !customError ? (
                  <img
                    src={customPreviewUrl}
                    alt={t("avatar-wizard-custom-preview-alt", "Vista previa del avatar")}
                    className={cn(
                      "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
                      customLoading ? "opacity-50" : "opacity-100"
                    )}
                  />
                ) : customLoading ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin text-primary/60" />
                    <p className="text-xs">{t("avatar-wizard-custom-generating", "Generando preview...")}</p>
                  </div>
                ) : customError ? (
                  <div className="absolute inset-0 flex items-center justify-center px-5 text-center text-muted-foreground">
                    <div>
                      <p className="text-sm font-medium">
                        {t("avatar-wizard-custom-error", "No pudimos generar el preview")}
                      </p>
                      <button
                        type="button"
                        onClick={() => regenerateCustomPreview(customConfig as UGCAvatarConfig)}
                        className="mt-2 text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <RefreshCw className="h-3 w-3" /> {t("video-voice-library-retry", "Reintentar")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground px-6 text-center">
                    <div className="h-11 w-11 rounded-full bg-white/80 flex items-center justify-center text-muted-foreground/70 shadow-sm">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <p className="text-[13px] font-semibold text-foreground leading-tight">
                      {customComplete
                        ? t("avatar-wizard-custom-ready", "Listo para generar")
                        : t("avatar-wizard-custom-empty-title", "Completa los campos")}
                    </p>
                    <p className="text-[11px] leading-snug max-w-[200px]">
                      {customComplete
                        ? t("avatar-wizard-custom-ready-desc", "Presiona 'Generar preview' para ver tu avatar.")
                        : `${customFilledCount} / 7 ${t("avatar-wizard-custom-filled-of", "campos completos")}`}
                    </p>
                  </div>
                )}
                {customPreviewUrl && customLoading && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/90 text-[10px] font-semibold text-primary shadow-sm">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("avatar-wizard-custom-refresh", "Actualizando")}
                  </div>
                )}
              </div>
            </div>

            {/* Config panel */}
            <div className="overflow-y-auto p-5 md:p-6 space-y-4">
              <AvatarBuilderModeTabs
                active="params"
                onSelect={(next) => setMode(next === "params" ? "custom" : "prompt")}
                t={t}
              />
              <BuilderSection
                label={t("avatar-gender", "Género")}
                options={AVATAR_GENDER_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.gender ?? ""}
                onChange={(v) => updateCustom({ gender: v as UGCAvatarConfig["gender"] })}
                columns={2}
              />
              <BuilderSection
                label={t("avatar-age", "Edad")}
                options={AVATAR_AGE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.age_range ?? ""}
                onChange={(v) => updateCustom({ age_range: v as UGCAvatarConfig["age_range"] })}
                columns={4}
              />
              <BuilderSwatches
                label={t("avatar-skin", "Tono de piel")}
                options={AVATAR_SKIN_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                  color: o.color,
                }))}
                value={customConfig.skin_tone ?? ""}
                onChange={(v) => updateCustom({ skin_tone: v as UGCAvatarConfig["skin_tone"] })}
              />
              <BuilderSection
                label={t("avatar-hair", "Cabello")}
                options={AVATAR_HAIR_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.hair ?? ""}
                onChange={(v) => updateCustom({ hair: v as UGCAvatarConfig["hair"] })}
                columns={2}
              />
              <BuilderSection
                label={t("avatar-hair-color", "Color de cabello")}
                options={AVATAR_HAIR_COLOR_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.hair_color ?? ""}
                onChange={(v) => updateCustom({ hair_color: v as UGCAvatarConfig["hair_color"] })}
                columns={2}
              />
              <BuilderSection
                label={t("avatar-vibe", "Vibe")}
                options={AVATAR_VIBE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.vibe ?? ""}
                onChange={(v) => updateCustom({ vibe: v as UGCAvatarConfig["vibe"] })}
                columns={2}
              />
              <BuilderSection
                label={t("avatar-setting", "Escenario")}
                options={AVATAR_SETTING_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.setting ?? ""}
                onChange={(v) => updateCustom({ setting: v as UGCAvatarConfig["setting"] })}
                columns={2}
              />

              {/* Phase 2 V2 Fase 2 — optional axes. Adding them lets the
                  user lock lighting / finish; skipping them leaves the
                  backend rotating the variant per generation (Fase 1). */}
              <OptionalBuilderSection
                label={t("avatar-photographic-style", "Estilo fotográfico")}
                autoLabel={t("avatar-auto", "Auto")}
                options={PHOTOGRAPHIC_STYLE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.photographic_style ?? ""}
                onChange={(v) =>
                  updateCustom({
                    photographic_style: (v || undefined) as UGCAvatarConfig["photographic_style"],
                  })
                }
                columns={3}
              />
              <OptionalBuilderSection
                label={t("avatar-skin-finish", "Acabado de piel")}
                autoLabel={t("avatar-auto", "Auto")}
                options={SKIN_FINISH_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey, o.value),
                }))}
                value={customConfig.skin_finish ?? ""}
                onChange={(v) =>
                  updateCustom({
                    skin_finish: (v || undefined) as UGCAvatarConfig["skin_finish"],
                  })
                }
                columns={4}
              />
            </div>
          </motion.div>
        )}

        {mode === "prompt" && (
          <motion.div
            key="prompt"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="flex-1 grid grid-cols-1 md:grid-cols-[minmax(0,0.52fr)_minmax(0,0.48fr)] overflow-hidden"
          >
            {/* Preview stage — same empty-state pattern as custom mode:
                nothing renders until the user writes a prompt AND
                presses "Generar avatar". */}
            <div className="bg-gradient-to-br from-muted/30 to-muted/10 flex items-center justify-center p-7 border-r border-border/60">
              <div className="relative aspect-[9/16] w-full max-w-[340px] rounded-3xl overflow-hidden bg-muted/50 shadow-xl">
                {promptPreviewUrl && !promptError ? (
                  <>
                    <img
                      src={promptPreviewUrl}
                      alt={t("avatar-wizard-custom-preview-alt", "Vista previa del avatar")}
                      className={cn(
                        "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
                        promptLoading ? "opacity-50" : "opacity-100"
                      )}
                    />
                    {promptNeedsRefresh && (
                      <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-white/92 px-4 py-3 text-left shadow-xl ring-1 ring-black/5">
                        <p className="text-[12px] font-semibold text-gray-900">
                          {t("avatar-wizard-prompt-image-unchanged", "Imagen anterior visible")}
                        </p>
                        <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
                          {t(
                            "avatar-wizard-prompt-image-unchanged-desc",
                            "Genera de nuevo para aplicar los cambios del texto."
                          )}
                        </p>
                      </div>
                    )}
                  </>
                ) : promptLoading ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin text-primary/60" />
                    <p className="text-xs">{t("avatar-wizard-prompt-generating-avatar", "Generando avatar...")}</p>
                  </div>
                ) : promptError ? (
                  <div className="absolute inset-0 flex items-center justify-center px-5 text-center text-muted-foreground">
                    <div>
                      <p className="text-sm font-medium">
                        {t("avatar-wizard-custom-error", "No pudimos generar el preview")}
                      </p>
                      <button
                        type="button"
                        onClick={() => regeneratePromptPreview(promptText)}
                        className="mt-2 text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <RefreshCw className="h-3 w-3" /> {t("video-voice-library-retry", "Reintentar")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground px-6 text-center">
                    <div className="h-11 w-11 rounded-full bg-white/80 flex items-center justify-center text-muted-foreground/70 shadow-sm">
                      <Pencil className="h-5 w-5" />
                    </div>
                    <p className="text-[13px] font-semibold text-foreground leading-tight">
                      {promptValid
                        ? t("avatar-wizard-prompt-ready", "Listo para generar")
                        : t("avatar-wizard-prompt-empty-title", "Escribe tu descripción")}
                    </p>
                    <p className="text-[11px] leading-snug max-w-[220px]">
                      {t(
                        "avatar-wizard-prompt-hint-clean",
                        "Pega o escribe la descripción completa del avatar."
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Prompt editor */}
            <div className="overflow-y-auto p-5 md:p-6 flex flex-col">
              <AvatarBuilderModeTabs
                active="prompt"
                onSelect={(next) => setMode(next === "params" ? "custom" : "prompt")}
                t={t}
              />
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                {t("avatar-wizard-prompt-label", "Descripción del avatar")}
              </label>
              <textarea
                value={promptText}
                onChange={(e) => {
                  const next = e.target.value;
                  setPromptText(next);
                  setPromptError(false);
                  setPromptRegenerationOpen(false);
                  setPromptRegenerationNote("");
                }}
                placeholder={t(
                  "avatar-wizard-prompt-placeholder",
                  "Ej: Mujer latina de 30, piel medium con subtono cálido dorado, textura glass-skin dewy con peach fuzz, pecas sutiles, cabello largo ondulado negro con flyaways a contraluz, top blanco de tirantes, aros dorados finos, luz solar de hora dorada creando sombras duras, cámara iPhone 15 Pro selfie, ligero ángulo picado, actitud confiada y relajada…"
                )}
                rows={10}
                className="w-full min-h-[240px] rounded-xl bg-muted/30 border border-transparent px-3 py-2.5 text-[13px] leading-5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary/40 focus:bg-white focus:ring-1 focus:ring-primary/20 outline-none transition resize-none"
              />
              {promptPreviewUrl && !promptNeedsRefresh && promptRegenerationOpen && (
                <div className="mt-4">
                  <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                    {t("avatar-wizard-prompt-regen-label", "Ajuste opcional para regenerar")}
                  </label>
                  <textarea
                    value={promptRegenerationNote}
                    onChange={(event) => setPromptRegenerationNote(event.target.value)}
                    placeholder={t(
                      "avatar-wizard-prompt-regen-placeholder",
                      "Ej: hazla más natural, cambia la luz a cocina de mañana, conserva el rostro pero mejora las manos..."
                    )}
                    rows={3}
                    className="w-full rounded-xl bg-muted/30 border border-transparent px-3 py-2.5 text-[13px] leading-5 text-foreground placeholder:text-muted-foreground/55 focus:border-primary/40 focus:bg-white focus:ring-1 focus:ring-primary/20 outline-none transition resize-none"
                  />
                </div>
              )}
            </div>
          </motion.div>
        )}

        {mode === "upload" && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, x: -10 }}
            className="flex-1 overflow-y-auto p-6"
          >
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) handleUploadFile(file);
                event.currentTarget.value = "";
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,0.52fr)_minmax(0,0.48fr)] gap-6 max-w-5xl mx-auto">
              <button
                type="button"
                onClick={() => uploadInputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const file = event.dataTransfer.files?.[0];
                  if (file) handleUploadFile(file);
                }}
                className={cn(
                  "relative aspect-[4/5] min-h-[420px] rounded-3xl border border-dashed transition overflow-hidden",
                  uploadPreviewUrl
                    ? "border-indigo-200 bg-gray-50"
                    : "border-indigo-200 bg-gradient-to-b from-white to-indigo-50/50 hover:border-indigo-400 hover:bg-indigo-50"
                )}
              >
                {uploadPreviewUrl ? (
                  <>
                    <img
                      src={uploadPreviewUrl}
                      alt={uploadDisplayName || t("avatar-wizard-upload-default-name", "Mi avatar")}
                      className={cn(
                        "absolute inset-0 h-full w-full object-cover transition duration-300",
                        uploadLoading && "scale-[1.02] blur-sm opacity-70"
                      )}
                    />
                    {uploadLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/15">
                        <div className="rounded-2xl bg-white/90 px-4 py-3 shadow-xl ring-1 ring-black/5 inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
                          <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                          {t("avatar-wizard-upload-uploading", "Subiendo avatar...")}
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/25 to-transparent text-left">
                      <p className="text-white text-sm font-semibold leading-tight">
                        {uploadS3Url
                          ? t("avatar-wizard-upload-ready", "Avatar listo")
                          : t("avatar-wizard-upload-preview", "Vista previa")}
                      </p>
                      {uploadFileName && (
                        <p className="text-white/75 text-[11px] leading-tight truncate mt-0.5">
                          {uploadFileName}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
                    <div className="h-14 w-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center ring-1 ring-indigo-100">
                      <Upload className="h-7 w-7" />
                    </div>
                    <p className="mt-5 text-base font-semibold text-gray-900">
                      {t("avatar-wizard-upload-drop-title", "Sube tu imagen")}
                    </p>
                    <p className="mt-2 text-sm text-gray-500 leading-relaxed max-w-[260px]">
                      {t(
                        "avatar-wizard-upload-drop-desc",
                        "Arrastra una foto o selecciónala desde tu equipo. Recomendado: rostro visible, buena luz y formato vertical."
                      )}
                    </p>
                    <span className="mt-5 inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-indigo-600 shadow-sm ring-1 ring-indigo-100">
                      {t("avatar-wizard-upload-select-file", "Seleccionar imagen")}
                    </span>
                  </div>
                )}
              </button>

              <div className="flex flex-col justify-center">
                <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-500">
                    {t("avatar-wizard-upload-panel-eyebrow", "Avatar propio")}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold text-gray-950">
                    {t("avatar-wizard-upload-panel-title", "Guárdalo en tu biblioteca")}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-500">
                    {t(
                      "avatar-wizard-upload-panel-desc",
                      "Lo usaremos como referencia visual para mantener la identidad del avatar en los videos."
                    )}
                  </p>

                  <label className="mt-5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    {t("avatar-wizard-upload-name-label", "Nombre")}
                  </label>
                  <input
                    value={uploadDisplayName}
                    onChange={(event) => setUploadDisplayName(event.target.value)}
                    className="mt-2 h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-medium text-gray-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                    placeholder={t("avatar-wizard-upload-name-placeholder", "Ej. Mi avatar")}
                  />

                  {uploadError && (
                    <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
                      {uploadError}
                    </p>
                  )}

                  <div className="mt-5 rounded-2xl bg-gray-50 p-4 text-left">
                    <p className="text-[12px] font-semibold text-gray-800">
                      {t("avatar-wizard-upload-disclaimer-title", "Importante")}
                    </p>
                    <p className="mt-1 text-[12px] leading-relaxed text-gray-500">
                      {t(
                        "avatar-wizard-upload-disclaimer",
                        "Usa fotos propias o de personas que te dieron autorización para aparecer en contenido publicitario."
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </>

      {/* Footer actions by mode */}
      {mode === "mine" && myAvatars.length > 0 && (
        <div className="px-5 py-3 border-t border-border/60 bg-muted/20 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setMode("preset")}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted/50"
          >
            {t("video-preview-back-to-studio", "Volver")}
          </button>
          <button
            type="button"
            disabled={selectedMineId === null}
            onClick={() => {
              const a = myAvatars.find((x) => x.id === selectedMineId);
              if (a) commitMine(a);
            }}
            className={cn(
              "px-5 py-2 rounded-lg text-sm font-semibold transition",
              selectedMineId !== null
                ? "bg-primary text-white hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            {t("avatar-wizard-use-avatar", "Usar este avatar")}
          </button>
        </div>
      )}
      {mode === "preset" && (() => {
        const selectedRecommendation = selectedRecommendationId
          ? usableGeneratedRecommendations.find((item) => item.id === selectedRecommendationId)
          : null;
        const selectedPreset = selectedPresetId ? PRESETS.find((p) => p.id === selectedPresetId) : null;
        const hasSelection = !!selectedRecommendation || !!selectedPreset;
        return (
          <div
            className={cn(
              "px-6 py-4 border-t border-gray-100 bg-white flex items-center gap-4",
              hasSelection ? "justify-between" : "justify-end"
            )}
          >
            {/* Preset footer keeps only the continuation action. Creation/upload
                live in the header, and saving happens from the card hover star. */}
            {hasSelection && (
              <div className="flex items-center gap-3 min-w-0">
                {selectedRecommendation ? (
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg overflow-hidden ring-2 ring-indigo-500 flex-shrink-0">
                    <img
                      src={selectedRecommendation.preview_url}
                      alt={selectedRecommendation.display_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-gray-900 leading-tight truncate">
                      {getAvatarCreativeTitle(selectedRecommendation.role)}
                    </p>
                    <p className="text-[11px] text-gray-500 leading-tight truncate">
                      {t("avatar-wizard-ai-recommended", "Recomendado por IA")}
                    </p>
                  </div>
                </div>
                ) : selectedPreset ? (
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg overflow-hidden ring-2 ring-indigo-500 flex-shrink-0">
                    <img src={selectedPreset.thumbnail} alt={selectedPreset.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-gray-900 leading-tight truncate">{selectedPreset.name}</p>
                    <p className="text-[11px] text-gray-500 leading-tight truncate">{selectedPreset.subtitle}</p>
                  </div>
                </div>
                ) : null}
              </div>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                disabled={!hasSelection}
                onClick={() => {
                  if (selectedRecommendation) {
                    commitRecommendation(selectedRecommendation);
                    return;
                  }
                  const preset = PRESETS.find((p) => p.id === selectedPresetId);
                  if (preset) commitPreset(preset);
                }}
                className={cn(
                  "px-5 py-2 rounded-lg text-sm font-semibold text-white transition inline-flex items-center gap-1.5",
                  hasSelection
                    ? "hover:brightness-110"
                    : "opacity-40 cursor-not-allowed"
                )}
                style={{
                  background: "linear-gradient(135deg, #4368E5, #2F6EDA)",
                  boxShadow: hasSelection ? "0 4px 12px rgba(47,110,218,0.3)" : undefined,
                }}
              >
                {t("avatar-wizard-use-avatar", "Usar este avatar")}
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </button>
            </div>
          </div>
        );
      })()}
      {mode === "custom" && (
        <div className="px-5 py-3 border-t border-border/60 bg-muted/20 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMode("preset")}
              className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted/50"
            >
              {t("avatar-wizard-back-to-library", "Volver a la biblioteca")}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* When there's no portrait yet, the primary action is
                "Generar preview". Once rendered, it becomes "Regenerar"
                (secondary) + "Usar este avatar" (primary). This way we
                never fire a Gemini call implicitly. */}
            {!customPreviewUrl ? (
              <button
                type="button"
                disabled={!customComplete || customLoading}
                onClick={() => regenerateCustomPreview(customConfig as UGCAvatarConfig)}
                className={cn(
                  "inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition",
                  customComplete && !customLoading
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-muted text-muted-foreground cursor-not-allowed"
                )}
              >
                {customLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <Sparkles className="h-3.5 w-3.5" />
                {t("avatar-wizard-custom-generate", "Generar preview")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={customLoading}
                  onClick={() => regenerateCustomPreview(customConfig as UGCAvatarConfig)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-border text-foreground/80 hover:border-primary/40 hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {customLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {t("avatar-wizard-custom-regenerate", "Regenerar")}
                </button>
                <SaveToCastButton
                  disabled={!customComplete || customLoading || savingAvatar}
                  saving={savingAvatar}
                  justSaved={justSavedId !== null}
                  labelIdle={t("avatar-wizard-save-to-cast", "Guardar en mi elenco")}
                  labelSaving={t("avatar-wizard-saving", "Guardando…")}
                  labelSaved={t("avatar-wizard-saved", "Guardado")}
                  onClick={handleSaveCustom}
                />
                <button
                  type="button"
                  disabled={customLoading}
                  onClick={commitCustom}
                  className={cn(
                    "px-5 py-2 rounded-lg text-sm font-semibold transition",
                    !customLoading
                      ? "bg-primary text-white hover:bg-primary/90"
                      : "bg-muted text-muted-foreground cursor-not-allowed"
                  )}
                >
                  {t("avatar-wizard-use-avatar", "Usar este avatar")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {mode === "prompt" && (
        <div className="px-5 py-3 border-t border-border/60 bg-muted/20 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setMode("preset")}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted/50"
          >
            {t("avatar-wizard-back-to-library", "Volver a la biblioteca")}
          </button>
          <div className="flex items-center gap-2">
            {!promptPreviewUrl || promptNeedsRefresh ? (
              <button
                type="button"
                disabled={!promptValid || promptLoading}
                onClick={() => regeneratePromptPreview(promptText, promptRegenerationNote)}
                className={cn(
                  "inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition",
                  promptValid && !promptLoading
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-muted text-muted-foreground cursor-not-allowed"
                )}
              >
                {promptLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <Sparkles className="h-3.5 w-3.5" />
                {t("avatar-wizard-prompt-generate-avatar", "Generar avatar")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={promptLoading}
                  onClick={() => {
                    if (!promptRegenerationOpen) {
                      setPromptRegenerationOpen(true);
                      return;
                    }
                    regeneratePromptPreview(promptText, promptRegenerationNote);
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-border text-foreground/80 hover:border-primary/40 hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {promptLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {promptRegenerationOpen
                    ? t("avatar-wizard-prompt-regenerate-now", "Regenerar avatar")
                    : t("avatar-wizard-custom-regenerate", "Regenerar")}
                </button>
                <SaveToCastButton
                  disabled={!promptValid || promptLoading || savingAvatar || promptNeedsRefresh}
                  saving={savingAvatar}
                  justSaved={justSavedId !== null}
                  labelIdle={t("avatar-wizard-save-to-cast", "Guardar en mi elenco")}
                  labelSaving={t("avatar-wizard-saving", "Guardando…")}
                  labelSaved={t("avatar-wizard-saved", "Guardado")}
                  onClick={handleSavePrompt}
                />
                <button
                  type="button"
                  disabled={promptLoading || promptNeedsRefresh}
                  onClick={commitPrompt}
                  className={cn(
                    "px-5 py-2 rounded-lg text-sm font-semibold transition",
                    !promptLoading
                      ? "bg-primary text-white hover:bg-primary/90"
                      : "bg-muted text-muted-foreground cursor-not-allowed"
                  )}
                >
                  {t("avatar-wizard-use-avatar", "Usar este avatar")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {mode === "upload" && (
        <div className="px-5 py-3 border-t border-border/60 bg-white flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setMode("preset")}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted/50"
          >
            {t("avatar-wizard-back-to-library", "Volver a la biblioteca")}
          </button>
          <div className="flex items-center gap-2">
            {uploadS3Url && (
              <button
                type="button"
                onClick={() => uploadInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-border text-foreground/80 hover:border-primary/40 hover:bg-primary/5 transition"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {t("avatar-wizard-upload-change", "Cambiar imagen")}
              </button>
            )}
            <SaveToCastButton
              disabled={!uploadS3Url || uploadLoading || savingAvatar}
              saving={savingAvatar}
              justSaved={justSavedId !== null}
              labelIdle={t("avatar-wizard-save-to-cast", "Guardar en mi elenco")}
              labelSaving={t("avatar-wizard-saving", "Guardando…")}
              labelSaved={t("avatar-wizard-saved", "Guardado")}
              onClick={handleSaveUpload}
            />
            <button
              type="button"
              disabled={!uploadS3Url || uploadLoading}
              onClick={commitUpload}
              className={cn(
                "px-5 py-2 rounded-lg text-sm font-semibold transition inline-flex items-center gap-1.5",
                uploadS3Url && !uploadLoading
                  ? "bg-primary text-white hover:bg-primary/90"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              )}
            >
              {t("avatar-wizard-use-avatar", "Usar este avatar")}
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

function SaveToCastButton({
  disabled,
  saving,
  justSaved,
  labelIdle,
  labelSaving,
  labelSaved,
  onClick,
}: {
  disabled: boolean;
  saving: boolean;
  justSaved: boolean;
  labelIdle: string;
  labelSaving: string;
  labelSaved: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition",
        disabled
          ? "border-border/50 text-muted-foreground cursor-not-allowed"
          : justSaved
          ? "border-emerald-300 text-emerald-700 bg-emerald-50"
          : "border-border text-foreground/80 hover:border-primary/40 hover:bg-primary/5"
      )}
    >
      {saving ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : justSaved ? (
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      ) : (
        <BookmarkPlus className="h-3.5 w-3.5" />
      )}
      {saving ? labelSaving : justSaved ? labelSaved : labelIdle}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────

function AvatarRecommendationLoadingCard({
  title,
  index,
}: {
  title: string;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.42, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="relative aspect-[9/16] w-full max-w-[180px] rounded-xl"
      style={{
        padding: "1.5px",
        background:
          "linear-gradient(135deg, rgba(77,181,255,0.36), rgba(67,104,229,0.46), rgba(47,110,218,0.30), rgba(67,104,229,0.42))",
        backgroundSize: "300% 300%",
        animation: "gradientShift 4s ease infinite",
        boxShadow: "0 10px 30px rgba(67,104,229,0.08)",
      }}
    >
      <div className="relative h-full overflow-hidden rounded-[11px] bg-gradient-to-b from-[#fbfbff] via-white to-indigo-50">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -inset-y-10 w-[72%] skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/75 to-transparent blur-sm"
            initial={{ x: "-150%", opacity: 0 }}
            animate={{ x: ["-150%", "190%"], opacity: [0, 0.9, 0] }}
            transition={{
              delay: 0.35 + index * 0.18,
              duration: 2.45,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 0.85,
            }}
          />
          <motion.div
            className="absolute -inset-y-12 w-[28%] skew-x-[-18deg] bg-white/35 blur-md"
            initial={{ x: "-180%", opacity: 0 }}
            animate={{ x: ["-180%", "230%"], opacity: [0, 0.55, 0] }}
            transition={{
              delay: 0.7 + index * 0.18,
              duration: 2.2,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 1.1,
            }}
          />
          <div className="absolute inset-x-8 top-[18%] h-48 rounded-full bg-indigo-200/25 blur-3xl" />
          <div className="absolute inset-x-8 bottom-[14%] h-36 rounded-full bg-sky-100/55 blur-3xl" />
        </div>

        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-8 text-center">
          <AvatarCreationSpinner index={index} />
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.08 }}
            className="mt-5 text-[14px] font-semibold leading-tight text-slate-900"
          >
            {title}
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
}

function AvatarCreationSpinner({ index }: { index: number }) {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const gradientId = `avatarSpinGrad-${index}`;

  return (
    <motion.div
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      className="relative"
      style={{ width: 72, height: 72 }}
    >
      <motion.svg
        className="absolute inset-0"
        width={72}
        height={72}
        viewBox="0 0 72 72"
        animate={{ rotate: 360 }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
      >
        <circle cx={36} cy={36} r={r} fill="none" stroke="#E8E8F8" strokeWidth={3} />
        <circle
          cx={36}
          cy={36}
          r={r}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={`${circ * 0.3} ${circ * 0.7}`}
        />
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4368E5" />
            <stop offset="100%" stopColor="#6B86F5" />
          </linearGradient>
        </defs>
      </motion.svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-fluxi text-white shadow-lg shadow-[#4368E5]/20">
          <Sparkles className="h-5 w-5" />
        </div>
      </div>
    </motion.div>
  );
}

function AvatarBuilderModeTabs({
  active,
  onSelect,
  t,
}: {
  active: "params" | "prompt";
  onSelect: (mode: "params" | "prompt") => void;
  t: (key: string, fallback?: string) => string;
}) {
  const options = [
    {
      value: "params" as const,
      label: t("avatar-wizard-builder-tab-params", "Por parámetros"),
      icon: SlidersHorizontal,
    },
    {
      value: "prompt" as const,
      label: t("avatar-wizard-builder-tab-prompt", "Con prompt IA"),
      icon: Sparkles,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted/50 p-1 mb-5">
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = active === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={cn(
              "h-11 w-full min-w-0 rounded-lg flex items-center justify-center gap-2 px-3 text-[13px] font-semibold leading-none transition-all",
              isActive
                ? "bg-primary text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-white/70"
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 text-center">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function BuilderSection({
  label,
  options,
  value,
  onChange,
  columns = 2,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
  columns?: number;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
        {label}
      </p>
      <div className={cn("grid gap-1.5", columns === 4 ? "grid-cols-4" : "grid-cols-2")}>
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                "py-2 px-2.5 rounded-lg text-[11px] font-medium border transition",
                active
                  ? "bg-primary/10 text-primary border-primary/40"
                  : "bg-white text-foreground/80 border-border/60 hover:border-primary/30"
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Same visual language as BuilderSection but with an explicit "Auto" pill
 * at the start that sets the field to empty string ("") — meaning the
 * backend should rotate this axis randomly. Used for the Phase 2 V2 Fase 2
 * optional dimensions (photographic style, skin finish) so the user can
 * dial in or leave Fluxi to pick.
 */
function OptionalBuilderSection({
  label,
  autoLabel,
  options,
  value,
  onChange,
  columns = 3,
}: {
  label: string;
  autoLabel: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
  columns?: number;
}) {
  const autoActive = value === "";
  return (
    <div>
      <div className="flex items-baseline gap-1 mb-1.5">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          {label}
        </p>
        <span className="text-[9px] text-muted-foreground/60 lowercase">· opcional</span>
      </div>
      <div
        className={cn(
          "grid gap-1.5",
          columns === 4 ? "grid-cols-4" : columns === 3 ? "grid-cols-3" : "grid-cols-2",
        )}
      >
        <button
          type="button"
          onClick={() => onChange("")}
          className={cn(
            "py-2 px-2.5 rounded-lg text-[11px] font-medium border transition",
            autoActive
              ? "bg-primary/10 text-primary border-primary/40"
              : "bg-white text-foreground/60 border-border/60 hover:border-primary/30"
          )}
        >
          {autoLabel}
        </button>
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                "py-2 px-2.5 rounded-lg text-[11px] font-medium border transition",
                active
                  ? "bg-primary/10 text-primary border-primary/40"
                  : "bg-white text-foreground/80 border-border/60 hover:border-primary/30"
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BuilderSwatches({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string; color: string }>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
        {label}
      </p>
      <div className="flex gap-2">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              title={opt.label}
              aria-label={opt.label}
              className={cn(
                "relative h-9 w-9 rounded-full border-2 transition",
                active ? "border-primary scale-110 shadow-sm" : "border-border hover:border-primary/40"
              )}
              style={{ backgroundColor: opt.color }}
            >
              {active && (
                <Check className="absolute inset-0 m-auto h-3.5 w-3.5 text-white drop-shadow" strokeWidth={3} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Phase 6 V3c (Apr 20 2026) — AvatarWizardChoiceCard + peek components.
 * Ported from the Claude Design handoff bundle (fluxi 2 / ads-studio.html).
 * Single card component with 3 variants (orange / blue / purple) so the
 * 3 choices share identical layout/typography but get distinct color
 * tokens per variant. Kept inline-styled for the tokens that Tailwind
 * can't express cleanly (multi-stop linear gradients, box-shadow with
 * rgba from brand colors).
 * ────────────────────────────────────────────────────────────────────── */

type AvatarWizardChoiceVariant = "orange" | "blue" | "indigo" | "purple";

const VARIANT_TOKENS: Record<AvatarWizardChoiceVariant, {
  gradient: string;
  shadow: string;
  border: string;
  eyebrow: string;
}> = {
  orange: {
    gradient: "linear-gradient(145deg, #FFF7ED 0%, #FED7AA 40%, #FDBA74 100%)",
    shadow: "0 4px 20px rgba(251,146,60,.15)",
    border: "1px solid rgba(251,146,60,.2)",
    eyebrow: "#C2410C",
  },
  blue: {
    gradient: "linear-gradient(145deg, #EEF2FF 0%, #DBEAFE 40%, #FCE7F3 100%)",
    shadow: "0 4px 20px rgba(67,104,229,.12)",
    border: "none",
    eyebrow: "rgba(67,104,229,.85)",
  },
  indigo: {
    gradient: "linear-gradient(145deg, #F0F3FF 0%, #E0E7FF 40%, #EDE9FE 100%)",
    shadow: "0 4px 20px rgba(99,102,241,.12)",
    border: "1px solid rgba(99,102,241,.15)",
    eyebrow: "rgba(99,102,241,.85)",
  },
  purple: {
    gradient: "linear-gradient(160deg, #FAF5FF 0%, #F3E8FF 100%)",
    shadow: "0 4px 20px rgba(168,85,247,.08)",
    border: "1px solid rgba(168,85,247,.2)",
    eyebrow: "rgba(147,51,234,.7)",
  },
};

function AvatarWizardChoiceCard({
  variant,
  eyebrow,
  title,
  titleSecondLine,
  badge,
  cta,
  ctaColor,
  caption,
  visual,
  onClick,
}: {
  variant: AvatarWizardChoiceVariant;
  eyebrow: string;
  title: string;
  titleSecondLine?: string;
  badge: React.ReactNode;
  cta: string;
  ctaColor: string;
  caption: string;
  visual: React.ReactNode;
  onClick: () => void;
}) {
  const tokens = VARIANT_TOKENS[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative rounded-2xl overflow-hidden text-left transition-all hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
      style={{
        background: tokens.gradient,
        boxShadow: tokens.shadow,
        border: tokens.border,
        minHeight: 360,
      }}
    >
      {/* Badge — top right */}
      <div className="absolute top-4 right-4 z-10">{badge}</div>

      <div className="px-6 pt-6 pb-5 flex flex-col h-full">
        {/* Eyebrow */}
        <p
          className="text-[10px] font-bold tracking-wider mb-2 uppercase"
          style={{ color: tokens.eyebrow }}
        >
          {eyebrow}
        </p>

        {/* Title (optional second line for visual rhythm) */}
        <h3 className="text-[20px] font-bold leading-tight text-gray-900 pr-14">
          {title}
          {titleSecondLine && (
            <>
              <br />
              {titleSecondLine}
            </>
          )}
        </h3>

        {/* Visual area — fills the middle; fixed height so cards align */}
        <div className="flex-1 flex items-center justify-center my-3 min-h-[160px]">
          {visual}
        </div>

        {/* Caption */}
        <p className="text-[12px] text-gray-600 leading-relaxed mb-3">
          {caption}
        </p>

        {/* CTA */}
        <span
          className="text-[12px] font-semibold inline-flex items-center gap-1 transition-transform group-hover:translate-x-0.5"
          style={{ color: ctaColor }}
        >
          {cta}
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </button>
  );
}

/**
 * Animated "fan" of 5 presenter portraits with floating keyframes.
 * The center portrait is the hero (larger + ring-white). Four side slots
 * flank it with progressive rotation (-8°/-3°/+3°/+8°). Each slot uses
 * a distinct keyframe (fluxi-float-1..5) with offset delays so the fan
 * feels alive without looking busy.
 */
function PresentersFanPeek({ presets }: { presets: PresetAvatar[] }) {
  const slots = [
    { size: { w: 62, h: 88 }, left: "calc(50% - 112px)", z: 1, anim: "fluxi-float-1" },
    { size: { w: 76, h: 108 }, left: "calc(50% - 78px)",  z: 2, anim: "fluxi-float-2" },
    { size: { w: 96, h: 132 }, left: "calc(50% - 48px)", z: 3, anim: "fluxi-float-3", hero: true },
    { size: { w: 76, h: 108 }, left: "calc(50% + 2px)",  z: 2, anim: "fluxi-float-4" },
    { size: { w: 62, h: 88 }, left: "calc(50% + 50px)",  z: 1, anim: "fluxi-float-5" },
  ];
  return (
    <div className="relative w-full max-w-[250px] mx-auto" style={{ height: 170 }}>
      {slots.map((slot, i) => {
        const preset = presets[i];
        if (!preset) return null;
        return (
          <div
            key={preset.id}
            className={cn(
              "absolute rounded-xl overflow-hidden",
              slot.anim,
              slot.hero ? "shadow-2xl ring-4 ring-white/80" : "shadow-xl"
            )}
            style={{
              width: slot.size.w,
              height: slot.size.h,
              left: slot.left,
              top: "50%",
              zIndex: slot.z,
            }}
          >
            <img
              src={preset.thumbnail}
              alt={preset.name}
              className="w-full h-full object-cover"
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Visual peek for the "A tu medida, con IA" (INDIGO) card. Shows the two
 * construction modes at a glance — structured parameter pills (gender,
 * age, skin) on top, freeform prompt textbox below. Mirrors the mini
 * mockups in the Claude Design handoff screenshot.
 */
function ConstruyeAvatarAIPeek() {
  return (
    <div className="w-full max-w-[200px] flex flex-col gap-2.5">
      {/* Top mini-card — parameter pills (structured axes) */}
      <div
        className="rounded-lg px-3 py-2.5"
        style={{
          background: "rgba(255,255,255,0.75)",
          border: "1px solid rgba(99,102,241,0.2)",
          boxShadow: "0 2px 6px rgba(99,102,241,0.08)",
        }}
      >
        <p className="text-[9px] font-bold tracking-wider text-indigo-600/80 mb-1.5">
          RASGOS
        </p>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: "Mujer", active: true },
            { label: "25-35", active: true },
            { label: "Claro", active: false },
          ].map((p, i) => (
            <span
              key={i}
              className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold",
                p.active
                  ? "bg-indigo-500 text-white"
                  : "bg-white text-gray-600 ring-1 ring-gray-200",
              )}
            >
              {p.label}
            </span>
          ))}
        </div>
        <div className="flex gap-1 mt-1.5">
          <span className="text-[9px] text-gray-500">Hair</span>
          <span className="w-3 h-3 rounded-full bg-gradient-to-br from-yellow-200 to-amber-600 ring-1 ring-gray-200"></span>
          <span className="w-3 h-3 rounded-full bg-gradient-to-br from-amber-800 to-stone-900 ring-1 ring-gray-200"></span>
          <span className="w-3 h-3 rounded-full bg-gradient-to-br from-orange-400 to-red-600 ring-1 ring-gray-200"></span>
        </div>
      </div>

      {/* Bottom mini-card — freeform prompt textbox */}
      <div
        className="rounded-lg px-3 py-2.5"
        style={{
          background: "rgba(255,255,255,0.75)",
          border: "1px solid rgba(99,102,241,0.2)",
          boxShadow: "0 2px 6px rgba(99,102,241,0.08)",
        }}
      >
        <p className="text-[9px] font-bold tracking-wider text-indigo-600/80 mb-1.5 inline-flex items-center gap-1">
          <Sparkles className="h-2.5 w-2.5" />
          PROMPT
        </p>
        <p className="text-[10px] text-gray-600 leading-snug italic">
          "Mujer de 30, cabello castaño, sonriente, en una cocina luminosa…"
        </p>
      </div>
    </div>
  );
}

/**
 * Drop-zone placeholder with a purple gradient camera-icon tile centered.
 * The whole card is already a click target; the drop zone is purely
 * visual (backend for upload is in a follow-up).
 */
function UploadDropZonePeek() {
  return (
    <div
      className="w-full max-w-[150px] aspect-square rounded-2xl flex flex-col items-center justify-center gap-3 transition-all group-hover:border-purple-500 group-hover:bg-white/90"
      style={{
        background: "rgba(255,255,255,.7)",
        border: "2px dashed rgba(168,85,247,.4)",
      }}
    >
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
        style={{
          background: "linear-gradient(135deg, #A855F7, #9333EA)",
          boxShadow: "0 6px 16px rgba(168,85,247,.3)",
        }}
      >
        <Camera className="h-5 w-5 text-white" />
      </div>
      <div className="text-center px-3">
        <p className="text-[12px] font-semibold text-gray-800">
          Arrastra o elige
        </p>
        <p className="text-[10px] text-gray-500 mt-0.5">
          JPG, PNG (máx. 10MB)
        </p>
      </div>
    </div>
  );
}
